import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, createContext, useContext, ReactNode } from 'react';

import {
  Home,
  BarChart3,
  Activity,
  User,
  Utensils,
  Coffee,
  Soup,
  Cookie,
  Flame,
  Lightbulb,
  Beef,
  Wheat,
  Droplet,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Award,
  Scale,
  Zap,
  ZapOff,
  ArrowLeft,
  Weight,
  Trophy,
  Settings,
  Moon,
  Sun,
  Edit3,
  Target,
  Check,
  X,
  Trash2,
  ChevronLeft,
  UserPlus,
  Gift,
  Copy,
  Share2,
  Plus,
  Clock,
  Timer,
  Dumbbell,
  Bike,
  Footprints,
  Waves,
  Heart,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LineChart, Line, Area, AreaChart, CartesianGrid, ReferenceLine, Cell, LabelList, Tooltip } from 'recharts';
import { useApi } from './services/api';

// Helper: форматировать дату в локальном формате YYYY-MM-DD (без UTC конверсии)
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Telegram WebApp types
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        initDataUnsafe?: any;
        ready: () => void;
        expand: () => void;
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        openLink?: (url: string, options?: any) => void;
        colorScheme?: 'light' | 'dark';
        onEvent?: (event: string, callback: () => void) => void;
        offEvent?: (event: string, callback: () => void) => void;
        MainButton: {
          color: string;
          textColor: string;
        };
      };
    };
  }
}

function openExternalLink(url: string) {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.openLink) return tg.openLink(url, { try_instant_view: false });
  window.location.href = url;
}

// ===== TYPES =====
interface Meal {
  id: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  time: string;
  dishes?: Array<{
    name: string;
    calories: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    image?: string;
  }>;
}

interface DailyData {
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}

interface WeeklyData {
  day: string;
  calories: number;
  goal: number;
  water: number;
  protein: number;
  carbs: number;
  fats: number;
}

interface WeightData {
  date: string;
  weight: number;
  bodyFat?: number;
  muscleMass?: number;
}

interface ActivityData {
  id: string;
  type: string;
  duration: number;
  calories: number;
  date: string;
  distance?: number;
}

interface StatsPeriod {
  id: 'day' | 'week' | 'weight';
  label: string;
}

// ===== THEME PROVIDER =====
type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light');
  const [_userOverride, setUserOverride] = useState<boolean>(false);

  // Загружаем тему при инициализации
  useEffect(() => {
    // Проверяем, переопределял ли пользователь тему вручную
    const hasUserOverride = localStorage.getItem('themeUserOverride') === 'true';
    const savedTheme = localStorage.getItem('theme') as Theme;
    const tg = window.Telegram?.WebApp;

    if (hasUserOverride && savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      // Пользователь вручную выбрал тему - используем её
      setTheme(savedTheme);
      setUserOverride(true);
    } else if (tg?.colorScheme) {
      // Используем тему Telegram по умолчанию
      setTheme(tg.colorScheme as Theme);
    } else if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setTheme(savedTheme);
    } else {
      // Определяем тему по системным настройкам
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      setTheme(systemTheme);
    }

    // Слушаем изменения темы в Telegram (только если пользователь не переопределил)
    const handleThemeChanged = () => {
      const hasOverride = localStorage.getItem('themeUserOverride') === 'true';
      if (!hasOverride) {
        const newTheme = window.Telegram?.WebApp?.colorScheme;
        if (newTheme) {
          setTheme(newTheme as Theme);
        }
      }
    };

    if (tg) {
      tg.onEvent?.('themeChanged', handleThemeChanged);
    }

    return () => {
      if (tg) {
        tg.offEvent?.('themeChanged', handleThemeChanged);
      }
    };
  }, []);

  // Применяем тему к документу и сохраняем в localStorage
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Сохраняем в localStorage
    localStorage.setItem('theme', theme);

    // Обновляем цвета Telegram WebApp
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;

      if (theme === 'dark') {
        // Темная тема - темный фон
        tg.setHeaderColor('#7B7BA8');
        tg.setBackgroundColor('#1A1A1A');
        // Дополнительно можем установить цвет темы
        tg.MainButton.color = '#7B7BA8';
        tg.MainButton.textColor = '#FFFFFF';
      } else {
        // Светлая тема
        tg.setHeaderColor('#595880');
        tg.setBackgroundColor('#F8F9FA');
        tg.MainButton.color = '#595880';
        tg.MainButton.textColor = '#FFFFFF';
      }
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(current => {
      const newTheme = current === 'light' ? 'dark' : 'light';
      // Помечаем, что пользователь вручную выбрал тему
      localStorage.setItem('themeUserOverride', 'true');
      setUserOverride(true);
      return newTheme;
    });
  };

  const value: ThemeContextType = {
    theme,
    setTheme,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// ===== CALENDAR COMPONENT =====
interface CalendarProps {
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  onProfileClick: () => void;
  availableDates?: Set<string>;
}

function Calendar({ currentDate, onDateSelect, onProfileClick, availableDates }: CalendarProps) {
  const [dragProgress, setDragProgress] = useState(0); // 0 = collapsed, 1 = fully expanded
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragStartProgress, setDragStartProgress] = useState(0); // Прогресс на момент начала drag
  const [totalDragDelta, setTotalDragDelta] = useState(0); // Общее смещение от начала drag
  const startYRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const dayHeadersRef = useRef<HTMLDivElement>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();
  const maxDragDistance = 100;

  const [calendarLayout, setCalendarLayout] = useState(() => ({
    rowStepPx: 50,
    collapsedHeightPx: 220,
    expandedHeightPx: 430,
  }));

  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Текущая дата - исправляем для соответствия изображению (август 2025, 4 число)
  // Текущая дата из системы вместо зашитой демонстрационной
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const getWeekDates = useCallback((): Date[] => {
    // Неделя вокруг currentDate (Пн-Вс)
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day; // смещение к понедельнику
    start.setDate(start.getDate() + diff);
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [currentDate]);

  const getMonthDates = useCallback((): Date[] => {
    // Все дни месяца currentDate, c заполнением начала недели до Пн
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startDate = new Date(first);
    const firstDayOfWeek = first.getDay();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    startDate.setDate(first.getDate() - offset);
    const dates: Date[] = [];
    let cur = new Date(startDate);
    while (cur <= last || cur.getDay() !== 1) { // тянем до конца последней недели
      dates.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
      if (dates.length > 42) break; // защита от бесконечной
    }
    return dates;
  }, [currentDate]);

  const weekDates = useMemo(() => getWeekDates(), [getWeekDates]);
  const monthDates = useMemo(() => getMonthDates(), [getMonthDates]);

  const isToday = useCallback((date: Date) => {
    const now = new Date();
    return date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }, []);

  const isCurrentMonth = useCallback((date: Date) => {
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  }, [currentMonth, currentYear]);

  const isSelected = useCallback((date: Date) => {
    return date.toDateString() === currentDate.toDateString();
  }, [currentDate]);

  // Проверка, является ли дата будущей (после 4 августа 2025)
  const isFutureDate = useCallback((date: Date) => {
    const now = new Date();
    return date > now;
  }, []);

  // Плавная анимация автодовёрстывания
  const animateToProgress = useCallback((targetProgress: number) => {
    setIsAnimating(true);
    const startProgress = dragProgress;
    const startTime = Date.now();
    const duration = 400;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Используем easing функцию для плавности
      const easeProgress = 1 - Math.pow(1 - progress, 3); // cubic-out easing
      const currentProgress = startProgress + (targetProgress - startProgress) * easeProgress;

      setDragProgress(currentProgress);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setDragProgress(targetProgress);
        setIsAnimating(false);
        // Сбрасываем все состояния drag после завершения анимации
        setDragStartProgress(targetProgress);
        setTotalDragDelta(0);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [dragProgress]);

  // Очистка анимации при размонтировании
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const _handleTouchStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();

    // Останавливаем текущую анимацию если она идёт
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      setIsAnimating(false);
    }

    setIsDragging(true);
    // Запоминаем прогресс на момент начала drag
    setDragStartProgress(dragProgress);
    setTotalDragDelta(0);

    // Поддержка как touch, так и mouse событий
    if ('touches' in e) {
      startYRef.current = e.touches[0].clientY;
    } else {
      startYRef.current = e.clientY;
    }
  }, [dragProgress]);

  const _handleTouchMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging || isAnimating) return;

    e.preventDefault();

    let currentY: number;
    if ('touches' in e) {
      currentY = e.touches[0].clientY;
    } else {
      currentY = e.clientY;
    }

    // Считаем общее смещение от начала drag
    const deltaFromStart = currentY - startYRef.current;
    setTotalDragDelta(deltaFromStart);

    // Вычисляем новый прогресс на основе начального состояния и смещения
    let newProgress = dragStartProgress + (deltaFromStart / maxDragDistance);
    newProgress = Math.max(0, Math.min(1, newProgress));

    setDragProgress(newProgress);
  }, [isDragging, isAnimating, dragStartProgress, maxDragDistance]);

  const _handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    if (isAnimating) return;

    // УПРОЩЕННАЯ ЛОГИКА SNAP-TO
    const threshold = 30; // Минимальное смещение в пикселях для автодовёрстывания
    const isExpanding = totalDragDelta > 0; // движение вниз = раскрытие
    const isCollapsing = totalDragDelta < 0; // движение вверх = закрытие

    console.log('Snap decision:', {
      dragStartProgress: dragStartProgress.toFixed(2),
      currentProgress: dragProgress.toFixed(2),
      totalDragDelta: totalDragDelta.toFixed(1),
      isExpanding,
      isCollapsing,
      thresholdMet: Math.abs(totalDragDelta) > threshold
    });

    // Если пользователь потянул достаточно далеко
    if (Math.abs(totalDragDelta) > threshold) {
      if (isExpanding) {
        // Тянул вниз (раскрытие) - раскрыть полностью
        animateToProgress(1);
      } else if (isCollapsing) {
        // Тянул вверх (закрытие) - закрыть полностью
        animateToProgress(0);
      }
    } else {
      // Маленькое движение - вернуться к ближайшему состоянию
      if (dragProgress < 0.5) {
        animateToProgress(0);
      } else {
        animateToProgress(1);
      }
    }
  }, [dragProgress, dragStartProgress, totalDragDelta, isAnimating, animateToProgress]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isAnimating) return;
    setIsDragging(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    startYRef.current = clientY;
    setDragStartProgress(dragProgress);
    setTotalDragDelta(0);
  };

  // Добавляем глобальные обработчики для mouse событий
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging && !isAnimating) {
        e.preventDefault();

        // Считаем общее смещение от начала drag
        const deltaFromStart = e.clientY - startYRef.current;
        setTotalDragDelta(deltaFromStart);

        // Вычисляем новый прогресс на основе начального состояния и смещения
        let newProgress = dragStartProgress + (deltaFromStart / maxDragDistance);
        newProgress = Math.max(0, Math.min(1, newProgress));

        setDragProgress(newProgress);
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);

      if (isAnimating) return;

      // ТА ЖЕ УПРОЩЕННАЯ ЛОГИКА
      const threshold = 30;
      const isExpanding = totalDragDelta > 0;
      const isCollapsing = totalDragDelta < 0;

      console.log('Mouse snap decision:', {
        dragStartProgress: dragStartProgress.toFixed(2),
        currentProgress: dragProgress.toFixed(2),
        totalDragDelta: totalDragDelta.toFixed(1),
        isExpanding,
        isCollapsing,
        thresholdMet: Math.abs(totalDragDelta) > threshold
      });

      if (Math.abs(totalDragDelta) > threshold) {
        if (isExpanding) {
          animateToProgress(1);
        } else if (isCollapsing) {
          animateToProgress(0);
        }
      } else {
        if (dragProgress < 0.5) {
          animateToProgress(0);
        } else {
          animateToProgress(1);
        }
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragProgress, dragStartProgress, totalDragDelta, maxDragDistance, isAnimating, animateToProgress]);

  const handleDateSelect = useCallback((date: Date) => {
    onDateSelect(date);
  }, [onDateSelect]);

  // Interpolate between week and month dates based on drag progress
  // Находим индекс ряда текущей недели в месяце
  const currentWeekRowIndex = useMemo(() => {
    const weekStart = weekDates[0];
    for (let i = 0; i < monthDates.length; i += 7) {
      const rowStart = monthDates[i];
      if (rowStart && weekStart && rowStart.toDateString() === weekStart.toDateString()) {
        return Math.floor(i / 7);
      }
    }
    return 0;
  }, [weekDates, monthDates]);

  const displayDates = useMemo(() => {
    // Всегда используем хронологический порядок месяца
    return monthDates;
  }, [monthDates]);

  const measureCalendarLayout = useCallback(() => {
    const containerEl = containerRef.current;
    const gridWrapperEl = gridWrapperRef.current;
    const gridEl = gridRef.current;

    if (!containerEl || !gridWrapperEl || !gridEl) return;

    const containerTop = containerEl.getBoundingClientRect().top;
    const contentBottom = gridWrapperEl.getBoundingClientRect().bottom;
    const contentHeight = Math.max(0, contentBottom - containerTop);

    const handleHeight = dragHandleRef.current?.getBoundingClientRect().height ?? 0;
    const handleReserve = handleHeight > 0 ? handleHeight + 16 : 44; // запас под абсолютный drag-handle

    const totalRows = Math.max(1, Math.ceil(monthDates.length / 7));

    let rowStepPx = 50;
    const buttons = gridEl.querySelectorAll<HTMLElement>('button.calendar-day');
    if (buttons.length >= 8) {
      const step = buttons[7].getBoundingClientRect().top - buttons[0].getBoundingClientRect().top;
      if (Number.isFinite(step) && step > 0) rowStepPx = step;
    }

    const expandedHeightPx = Math.ceil(contentHeight + handleReserve);
    const collapsedHeightPx = Math.max(0, Math.ceil(expandedHeightPx - (totalRows - 1) * rowStepPx));

    setCalendarLayout((prev) => {
      if (
        Math.abs(prev.rowStepPx - rowStepPx) < 0.5 &&
        Math.abs(prev.collapsedHeightPx - collapsedHeightPx) < 0.5 &&
        Math.abs(prev.expandedHeightPx - expandedHeightPx) < 0.5
      ) {
        return prev;
      }
      return { rowStepPx, collapsedHeightPx, expandedHeightPx };
    });
  }, [monthDates.length]);

  useLayoutEffect(() => {
    measureCalendarLayout();
  }, [measureCalendarLayout]);

  useEffect(() => {
    const onResize = () => measureCalendarLayout();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureCalendarLayout]);

  // Динамическая высота календаря с плавной интерполяцией
  const calendarHeight = useMemo(() => {
    return (
      calendarLayout.collapsedHeightPx +
      (calendarLayout.expandedHeightPx - calendarLayout.collapsedHeightPx) * dragProgress
    );
  }, [calendarLayout, dragProgress]);

  return (
    <div className="mx-4 mt-4 mb-6">
      <div
        ref={containerRef}
        className="calendar-container gpu-accelerated"
        style={{
          height: `${calendarHeight}px`,
          transition: isDragging || isAnimating ? 'none' : 'height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
      >
        {/* Header */}
        <div ref={headerRef} className="flex items-center justify-between p-6 pb-2">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1">
              {monthNames[currentMonth]}
            </h2>
            <p className="text-white/70 text-sm">{currentYear}</p>
          </div>
          <button
            onClick={onProfileClick}
            className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center transition-all duration-150 hover:scale-105 active:scale-95"
          >
            <User className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Day Headers */}
        <div ref={dayHeadersRef} className="grid grid-cols-7 mb-2 px-4">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-sm font-medium text-white/60 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div ref={gridWrapperRef} className="px-4 pb-2 overflow-hidden">
          <div
            ref={gridRef}
            className="grid grid-cols-7 gap-y-2"
            style={{
              // При свёрнутом состоянии смещаем сетку вверх, чтобы текущая неделя была видна
              transform: `translateY(${-currentWeekRowIndex * calendarLayout.rowStepPx * (1 - dragProgress)}px)`,
              transition: isDragging || isAnimating ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            {displayDates.map((date, index) => {
              const futureDate = isFutureDate(date);
              const otherMonth = !isCurrentMonth(date) && dragProgress > 0.2;
              const rowIndex = Math.floor(index / 7);
              const isCurrentWeekRow = rowIndex === currentWeekRowIndex;
              const dateKey = formatLocalDate(date);
              const allowed = !availableDates || availableDates.size === 0 || availableDates.has(dateKey) || isToday(date) || new Date(dateKey) < new Date();

              // Calculate opacity: current week row always visible, others fade in with dragProgress
              let opacity = 1;
              if (!isCurrentWeekRow) {
                // Другие ряды появляются при раскрытии
                const totalRows = Math.ceil(monthDates.length / 7);
                const rowDistance = Math.abs(rowIndex - currentWeekRowIndex);
                // Чем дальше ряд от текущей недели, тем позже он появляется
                const rowAppearThreshold = rowDistance / totalRows;

                if (dragProgress < rowAppearThreshold) {
                  opacity = 0;
                } else {
                  // Плавное появление
                  opacity = Math.min(1, (dragProgress - rowAppearThreshold) / 0.3);
                }
              }

              return (
                <button
                  key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (allowed) handleDateSelect(date);
                  }}
                  className={`
                    calendar-day
                    ${!allowed ? 'disabled' : ''}
                    ${isSelected(date)
                      ? 'selected'
                      : isToday(date)
                        ? 'today'
                        : futureDate
                          ? 'future'
                          : otherMonth
                            ? 'other-month'
                            : ''
                    }
                  `}
                  style={{
                    opacity,
                    transform: opacity < 1 ? `scale(${0.8 + opacity * 0.2})` : 'scale(1)',
                    transition: isDragging || isAnimating ? 'none' : 'all 0.2s ease-out'
                  }}
                >
                  {date.getDate()}
                  {isToday(date) && !isSelected(date) && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Drag Handle */}
        <div
          ref={dragHandleRef}
          className="absolute bottom-2 left-0 right-0 flex justify-center py-2 cursor-grab active:cursor-grabbing touch-none z-10"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onClick={(e) => {
            e.stopPropagation();
            // Toggle if it was a click (not a drag)
            if (Math.abs(totalDragDelta) < 5) {
              animateToProgress(dragProgress < 0.5 ? 1 : 0);
            }
          }}
        >
          <div className="w-12 h-1.5 bg-white/20 rounded-full" />
        </div>
      </div>

      {/* Debug Info - Only visible if no user data */}
    </div>
  );
}



// ===== DAILY STATS COMPONENT =====
interface DailyStatsProps {
  totalCalories: number;
  goalCalories: number;
  meals: Meal[];
  protein: { current: number; goal: number };
  carbs: { current: number; goal: number };
  fats: { current: number; goal: number };
  dateLabel: string;
  hasTips: boolean;
  tipsEnabled: boolean;
  onAddMeal: (mealType: string) => void;
  onViewMeal: (meal: Meal) => void;
  onTipsClick: () => void;
}

function DailyStats({
  totalCalories,
  goalCalories,
  meals,
  protein,
  carbs,
  fats,
  dateLabel,
  hasTips,
  tipsEnabled,
  onAddMeal: _onAddMeal,
  onViewMeal: _onViewMeal,
  onTipsClick
}: DailyStatsProps) {
  const [_showMealDetails, _setShowMealDetails] = useState(false);
  const [expandedMeals, setExpandedMeals] = useState<string[]>([]);
  const [expandedDishIds, setExpandedDishIds] = useState<string[]>([]);

  const toggleMealExpansion = (mealId: string) => {
    setExpandedMeals(prev =>
      prev.includes(mealId)
        ? prev.filter(id => id !== mealId)
        : [...prev, mealId]
    );
  };

  const toggleDishExpansion = (dishKey: string) => {
    setExpandedDishIds(prev =>
      prev.includes(dishKey)
        ? prev.filter(k => k !== dishKey)
        : [...prev, dishKey]
    );
  };

  const handleDeleteMeal = async (mealIds: string | string[], e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!confirm('Удалить?')) return;

    try {
      const api = (window as any)._apiClient;
      if (!api) {
        console.error('API client not available');
        return;
      }

      const idsRaw = Array.isArray(mealIds) ? mealIds : [mealIds];
      const ids = Array.from(new Set(idsRaw.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)));
      if (ids.length === 0) return;

      const results = await Promise.allSettled(ids.map(id => api.deleteMiniAppMeal(id)));
      const fatal = results.find((r) => r.status === 'rejected' && !String((r as any).reason).includes('status: 404'));
      if (fatal && fatal.status === 'rejected') throw fatal.reason;

      // Trigger a reload of meals by dispatching a custom event
      window.dispatchEvent(new CustomEvent('meal-deleted'));
    } catch (error) {
      console.error('Failed to delete meal:', error);
      alert('Не удалось удалить приём пищи');
    }
  };

  const progressPercentage = (totalCalories / goalCalories) * 100;
  const remainingCalories = goalCalories - totalCalories;

  const mealTypes = [
    { id: 'breakfast', name: 'Завтрак', icon: Coffee, color: 'warning-gradient', iconColor: '#F39C12' },
    { id: 'lunch', name: 'Обед', icon: Soup, color: 'success-gradient', iconColor: '#27AE60' },
    { id: 'dinner', name: 'Ужин', icon: Utensils, color: 'info-gradient', iconColor: '#3498DB' },
    { id: 'snack', name: 'Перекус', icon: Cookie, color: 'secondary-gradient', iconColor: '#9B59B6' },
  ];

  const CircularProgress = ({
    percentage,
    size = 120,
    strokeWidth = 8,
    color = '#595880',
    children
  }: {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    color?: string;
    children?: React.ReactNode;
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            stroke="rgba(0,0,0,0.08)"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            stroke={color}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      </div>
    );
  };

  const MacroCard = ({
    label,
    current,
    goal,
    color,
    bgColor,
    textColor,
    icon: Icon
  }: {
    label: string;
    current: number;
    goal: number;
    color: string;
    bgColor: string;
    textColor: string;
    icon: any;
  }) => {
    const percentage = Math.min((current / goal) * 100, 100);

    return (
      <div className="premium-card p-4">
        <div className="flex flex-col items-center text-center">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mb-2"
            style={{ backgroundColor: bgColor }}
          >
            <Icon className="w-5 h-5 flex-shrink-0" style={{ color: textColor }} />
          </div>

          <h4 className="font-semibold text-xs mb-2" style={{ color: textColor }}>{label}</h4>

          <div className="mb-2">
            <CircularProgress
              percentage={percentage}
              size={56}
              strokeWidth={5}
              color={color}
            >
              <div className="text-center">
                <div className="text-base font-bold" style={{ color: textColor }}>{current}</div>
              </div>
            </CircularProgress>
          </div>

          <p className="text-xs text-muted-foreground">
            {current}г из {goal}г
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Главная карточка калорий */}
      <div className="premium-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
            >
              <Flame className="w-6 h-6 text-white flex-shrink-0" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">Калории</h3>
              <p className="text-muted-foreground text-sm">{dateLabel}</p>
            </div>
          </div>

          {/* Кнопка советов */}
          <button
            onClick={tipsEnabled ? onTipsClick : undefined}
            disabled={!tipsEnabled}
            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-150 flex-shrink-0 ${tipsEnabled
              ? (hasTips
                ? 'bg-warning text-white tips-bulb has-tips hover:scale-105 active:scale-95 cursor-pointer'
                : 'bg-section-bg text-warning hover:scale-105 active:scale-95 cursor-pointer')
              : 'bg-section-bg text-muted-foreground opacity-50 cursor-not-allowed'
              }`}
          >
            <Lightbulb className="w-6 h-6 flex-shrink-0" />
          </button>
        </div>

        <div className="flex items-center gap-6">
          <CircularProgress
            percentage={Math.max(0, Math.min(100, progressPercentage))}
            color="url(#calorieGradient)"
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{remainingCalories > 0 ? remainingCalories : 0}</div>
              <div className="text-xs text-muted-foreground">осталось</div>
            </div>
          </CircularProgress>

          <div className="flex-1">
            <div className="text-3xl font-bold mb-1">{totalCalories}</div>
            <div className="text-muted-foreground">из {goalCalories} ккал</div>
          </div>
        </div>

        <svg width="0" height="0">
          <defs>
            <linearGradient id="calorieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#595880" />
              <stop offset="100%" stopColor="#7B7BA8" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* БЖУ блоки */}
      <div className="grid grid-cols-3 gap-4">
        <MacroCard
          label="Белки"
          current={protein.current}
          goal={protein.goal}
          color="var(--protein-color)"
          bgColor="var(--protein-bg)"
          textColor="var(--protein-text)"
          icon={Beef}
        />
        <MacroCard
          label="Жиры"
          current={fats.current}
          goal={fats.goal}
          color="var(--fats-color)"
          bgColor="var(--fats-bg)"
          textColor="var(--fats-text)"
          icon={Droplet}
        />
        <MacroCard
          label="Углеводы"
          current={carbs.current}
          goal={carbs.goal}
          color="var(--carbs-color)"
          bgColor="var(--carbs-bg)"
          textColor="var(--carbs-text)"
          icon={Wheat}
        />
      </div>

      {/* Приемы пищи - только просмотр */}
      <div className="premium-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold">Приемы пищи</h3>
            <p className="text-sm text-muted-foreground">Добавление через бота</p>
          </div>
        </div>

        <div className="space-y-4">
          {mealTypes.map((mealType) => {
            // Группируем все приёмы по типу (перекусы, завтраки и т.д.)
            const group = meals.filter((m: any) => m.category === mealType.id);
            // Агрегируем значения для шапки карточки и собираем состав
            const meal = group.length > 0 ? (() => {
              const sum = group.reduce((acc: any, m: any) => ({
                calories: acc.calories + (m.calories || 0),
                protein: acc.protein + (m.protein || 0),
                carbs: acc.carbs + (m.carbs || 0),
                fats: acc.fats + (m.fats || 0)
              }), { calories: 0, protein: 0, carbs: 0, fats: 0 });
              const times = group.map((m: any) => m.time || new Date(m.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
              const dishes = group.map((m: any) => ({
                originalId: m.id,
                name: m.name,
                calories: Number(m.calories) || 0,
                protein: Number(m.protein) || 0,
                carbs: Number(m.carbs) || 0,
                fats: Number(m.fats) || 0,
                items: (Array.isArray(m.dishes) && m.dishes.length
                  ? m.dishes.map((d: any) => ({ name: d.name, calories: Number(d.calories) || 0, protein: Number(d.protein) || 0, carbs: Number(d.carbs) || 0, fats: Number(d.fats) || 0 }))
                  : (Array.isArray(m.aiAnalysis?.items) ? m.aiAnalysis.items.map((it: any) => ({ name: it.displayName || it.foodName || it.name, calories: Number(it.caloriesTotal ?? it.calories) || 0, protein: Number(it.proteinTotal ?? it.protein) || 0, carbs: Number(it.carbsTotal ?? it.carbs) || 0, fats: Number(it.fatsTotal ?? it.fats) || 0 })) : [])
                )
              }));
              return {
                id: mealType.id,
                time: times.join(', '),
                calories: sum.calories,
                protein: sum.protein,
                carbs: sum.carbs,
                fats: sum.fats,
                dishes
              };
            })() : undefined;
            const isExpanded = expandedMeals.includes(mealType.id);

            return (
              <div key={mealType.id} className="section-card p-4">
                <div
                  className="flex items-center gap-4 cursor-pointer"
                  onClick={() => toggleMealExpansion(mealType.id)}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 meal-icon-bg">
                    <mealType.icon className="w-6 h-6 flex-shrink-0" style={{ color: mealType.iconColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-lg">{mealType.name}</div>
                    {meal ? (
                      <div className="text-sm text-muted-foreground">
                        {meal.time}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">Не добавлено</div>
                    )}
                    {/* БЖУ слева */}
                    {meal && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className="text-[10px] font-medium" style={{ color: 'var(--protein-text)' }}>Б {meal.protein || 0}</div>
                        <div className="text-[10px] font-medium" style={{ color: 'var(--fats-text)' }}>Ж {meal.fats || 0}</div>
                        <div className="text-[10px] font-medium" style={{ color: 'var(--carbs-text)' }}>У {meal.carbs || 0}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {meal && (
                      <div className="text-right">
                        <div className="text-lg font-bold text-primary">{meal.calories}</div>
                        <div className="text-xs text-muted-foreground">ккал</div>
                      </div>
                    )}
                    {meal && (
                      <button
                        onClick={(e) => handleDeleteMeal(meal.dishes.map((d: any) => d.originalId), e)}
                        className="w-4 h-4 flex items-center justify-center rounded-full bg-destructive/10 hover:bg-destructive/20 transition-colors"
                        title="Удалить всё"
                      >
                        <Trash2 className="w-2.5 h-2.5 text-destructive flex-shrink-0" />
                      </button>
                    )}
                    <motion.div
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-section-bg/30"
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </motion.div>
                  </div>
                </div>

                {/* Раскрывающийся контент */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4">
                        {Array.isArray(meal?.dishes) && meal.dishes.length > 0 ? (
                          <div className="space-y-2">
                            {meal.dishes.map((dish: any, index: number) => {
                              const dishKey = `${mealType.id}-${index}-${dish.name}`;
                              const dishExpanded = expandedDishIds.includes(dishKey);
                              return (
                                <div className="relative mb-2 overflow-hidden rounded-xl">
                                  {/* Swipe Background */}
                                  <div className="absolute inset-0 bg-destructive flex items-center justify-end pr-4">
                                    <Trash2 className="w-5 h-5 text-white" />
                                  </div>

                                  <motion.div
                                    drag="x"
                                    dragConstraints={{ left: 0, right: 0 }}
                                    dragElastic={{ left: 0.1, right: 0.1 }}
                                    onDragEnd={(_, info) => {
                                      if (info.offset.x < -100) {
                                        handleDeleteMeal(dish.originalId);
                                      }
                                    }}
                                    className="bg-[var(--section-bg)] relative z-10"
                                    style={{ touchAction: 'pan-y' }}
                                  >
                                    <div
                                      className="w-full flex items-center justify-between p-3 gap-3 cursor-pointer"
                                      onClick={() => toggleDishExpansion(dishKey)}
                                    >
                                      <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                        {dish.image && (
                                          <img src={dish.image} alt={dish.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium break-words whitespace-normal">{dish.name}</div>
                                          <div className="flex items-center gap-2 mt-0.5">
                                            {dish.weight ? <span className="text-xs text-muted-foreground">{dish.weight}г</span> : null}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right flex-shrink-0 ml-2">
                                        <div className="font-semibold text-sm">{dish.calories}</div>
                                        <div className="text-xs text-muted-foreground">ккал</div>
                                        <div className="flex items-center gap-1 mt-1">
                                          <div className="text-[9px] font-medium" style={{ color: 'var(--protein-text)' }}>Б {dish.protein || 0}</div>
                                          <div className="text-[9px] font-medium" style={{ color: 'var(--fats-text)' }}>Ж {dish.fats || 0}</div>
                                          <div className="text-[9px] font-medium" style={{ color: 'var(--carbs-text)' }}>У {dish.carbs || 0}</div>
                                        </div>
                                      </div>
                                      <motion.div
                                        className="w-7 h-7 flex items-center justify-center rounded-full bg-section-bg/30"
                                        animate={{ rotate: dishExpanded ? 180 : 0 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                      </motion.div>
                                    </div>
                                    <AnimatePresence>
                                      {dishExpanded && Array.isArray(dish.items) && dish.items.length > 0 && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: 'auto', opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.2 }}
                                        >
                                          <div className="px-3 pb-3 mt-1 text-xs text-muted-foreground space-y-1">
                                            {dish.items.map((it: any, i: number) => (
                                              <div key={i} className="flex items-start justify-between gap-2">
                                                <span className="leading-4 whitespace-normal break-words pr-2">• {it.name || it.foodName}</span>
                                                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                                  <span className="text-[10px]" style={{ color: 'var(--protein-text)' }}>Б {Number(it.protein ?? it.proteinTotal ?? 0)}</span>
                                                  <span className="text-[10px]" style={{ color: 'var(--fats-text)' }}>Ж {Number(it.fats ?? it.fatsTotal ?? 0)}</span>
                                                  <span className="text-[10px]" style={{ color: 'var(--carbs-text)' }}>У {Number(it.carbs ?? it.carbsTotal ?? 0)}</span>
                                                  <span className="text-[10px] text-muted-foreground ml-1">{Number(it.calories ?? it.caloriesTotal ?? 0)} ккал</span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </motion.div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-muted-foreground">
                            <mealType.icon className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: mealType.iconColor }} />
                            <p className="text-sm">Блюда не добавлены</p>
                            <p className="text-xs mt-1">Добавьте через бота</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div >
      </div >
    </div >
  );
}

// ===== STATISTICS COMPONENT =====
interface StatisticsProps {
  dailyData: DailyData[];
  weeklyData: WeeklyData[];
  weightData: WeightData[];
  selectedDate: Date;
  targets?: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
}

function Statistics({ dailyData, weeklyData, weightData, selectedDate, targets }: StatisticsProps) {
  const [activePeriod, setActivePeriod] = useState<'day' | 'week' | 'weight'>('week');
  const [weightPoints, setWeightPoints] = useState<5 | 10 | 30 | 'all'>(5);

  // Helper to get CSS variable values for charts
  const getMacroColors = useCallback(() => {
    const style = getComputedStyle(document.documentElement);
    return {
      protein: style.getPropertyValue('--protein-text').trim() || '#B85450',
      fats: style.getPropertyValue('--fats-text').trim() || '#0C5460',
      carbs: style.getPropertyValue('--carbs-text').trim() || '#B8860B'
    };
  }, []);

  const macroColors = useMemo(() => getMacroColors(), [getMacroColors]);

  const periods: StatsPeriod[] = [
    { id: 'day', label: 'День' },
    { id: 'week', label: 'Неделя' },
    { id: 'weight', label: 'Вес' },
  ];

  const weightPointOptions: Array<{ id: 5 | 10 | 30 | 'all'; label: string }> = [
    { id: 5, label: '5' },
    { id: 10, label: '10' },
    { id: 30, label: '30' },
    { id: 'all', label: 'Все' },
  ];

  const getCurrentData = (): (DailyData | WeeklyData | WeightData)[] => {
    switch (activePeriod) {
      case 'day':
        // Prefer the day selected in the global calendar.
        // dailyData contains YYYY-MM-DD strings.
        const selectedKey = formatLocalDate(selectedDate);
        const selected = dailyData.find((d) => d.date === selectedKey);
        return [
          selected ||
          dailyData[dailyData.length - 1] ||
          ({ date: selectedKey, calories: 0, protein: 0, carbs: 0, fats: 0, meals: [] } as any),
        ];
      case 'week':
        return weeklyData;
      case 'weight':
        return weightData;
      default:
        return weeklyData;
    }
  };

  const getStatsCards = () => {
    const data = getCurrentData();

    switch (activePeriod) {
      case 'day':
        const today = data[0] as DailyData;
        return [
          {
            title: 'Калории',
            value: today?.calories || 0,
            target: targets?.calories ?? 0,
            unit: 'ккал',
            icon: Flame,
            color: '#595880'
          },
          {
            title: 'Белки',
            value: today?.protein || 0,
            target: targets?.protein ?? 0,
            unit: 'г',
            icon: Beef,
            color: 'var(--protein-text)'
          },
          {
            title: 'Жиры',
            value: today?.fats || 0,
            target: targets?.fats ?? 0,
            unit: 'г',
            icon: Droplet,
            color: 'var(--fats-text)'
          },
          {
            title: 'Углеводы',
            value: today?.carbs || 0,
            target: targets?.carbs ?? 0,
            unit: 'г',
            icon: Wheat,
            color: 'var(--carbs-text)'
          }
        ];
      case 'week':
        const avgProtein = Math.round(weeklyData.reduce((sum, day) => sum + day.protein, 0) / 7);
        const avgCarbs = Math.round(weeklyData.reduce((sum, day) => sum + day.carbs, 0) / 7);
        const avgFats = Math.round(weeklyData.reduce((sum, day) => sum + day.fats, 0) / 7);
        const avgCalories = Math.round(weeklyData.reduce((sum, day) => sum + day.calories, 0) / 7);
        return [
          {
            title: 'Среднее за день',
            value: avgCalories,
            target: targets?.calories ?? 0,
            unit: 'ккал',
            icon: Flame,
            color: '#595880'
          },
          {
            title: 'Белки в среднем',
            value: avgProtein,
            target: targets?.protein ?? 0,
            unit: 'г',
            icon: Beef,
            color: 'var(--protein-text)'
          },
          {
            title: 'Жиры в среднем',
            value: avgFats,
            target: targets?.fats ?? 0,
            unit: 'г',
            icon: Droplet,
            color: 'var(--fats-text)'
          },
          {
            title: 'Углеводы в среднем',
            value: avgCarbs,
            target: targets?.carbs ?? 0,
            unit: 'г',
            icon: Wheat,
            color: 'var(--carbs-text)'
          }
        ];
      case 'weight':
        const currentWeight = weightData.length > 0 ? weightData[weightData.length - 1].weight : 0;
        const startWeight = weightData.length > 0 ? weightData[0].weight : 0;
        const weeklyChange = weightData.length > 1 ?
          Math.round((weightData[weightData.length - 1].weight - weightData[weightData.length - 2].weight) * 10) / 10 : 0;
        const totalChange = Math.round((currentWeight - startWeight) * 10) / 10;

        // Настройки пользователя: берём из профиля миниаппа
        const targetWeight = (globalThis as any)._appUserData?.mini?.profile?.targetWeight ?? 0;
        const weightGoal: any = (globalThis as any)._appUserData?.mini?.profile?.goal ?? 'maintain';
        const targetBMI = (globalThis as any)._appUserData?.mini?.profile?.bmi;

        // Расчет ИМТ из реального роста/веса
        const height = (((globalThis as any)._appUserData?.mini?.profile?.height) ?? 0) / 100; // метры
        const bmi = height > 0 && currentWeight > 0 ? Math.round((currentWeight / (height * height)) * 10) / 10 : 0;

        // Логика для блока "Вес"
        const weightReachedTarget = Math.abs(currentWeight - targetWeight) <= 0.5;
        const weightColor = weightReachedTarget ? '#27AE60' : '#595880';

        // Логика для блока "За неделю"
        let weeklyIcon = TrendingUp;
        let weeklyColor = '#595880';
        let weeklyPrefix = '';

        if (weeklyChange < 0) {
          weeklyIcon = TrendingDown;
          weeklyPrefix = '';
          if (weightGoal === 'lose') weeklyColor = '#27AE60';
          else if (weightGoal === 'gain') weeklyColor = '#E74C3C';
          else weeklyColor = '#E74C3C'; // maintain
        } else if (weeklyChange > 0) {
          weeklyIcon = TrendingUp;
          weeklyPrefix = '+';
          if (weightGoal === 'lose') weeklyColor = '#E74C3C';
          else if (weightGoal === 'gain') weeklyColor = '#27AE60';
          else weeklyColor = '#E74C3C'; // maintain
        } else {
          weeklyIcon = TrendingUp;
          weeklyPrefix = '';
          if (weightGoal === 'maintain') weeklyColor = '#27AE60';
          else weeklyColor = '#595880';
        }

        // Логика для блока "Общее изменение"
        let totalPrefix = '';
        let totalColor = '#595880';
        if (totalChange < 0) {
          totalPrefix = '';
          if (weightGoal === 'lose') totalColor = '#27AE60';
          else if (weightGoal === 'gain') totalColor = '#E74C3C';
          else totalColor = '#595880';
        } else if (totalChange > 0) {
          totalPrefix = '+';
          if (weightGoal === 'lose') totalColor = '#E74C3C';
          else if (weightGoal === 'gain') totalColor = '#27AE60';
          else totalColor = '#595880';
        }

        // Логика для ИМТ
        let bmiColor = '#E74C3C'; // красный для избыточного/низкого
        if (bmi >= 18.5 && bmi <= 24.9) {
          // Нормальный диапазон - желтый
          bmiColor = '#F39C12';

          // Если достиг желаемого ИМТ - зеленый
          if (Math.abs(bmi - targetBMI) <= 0.5) {
            bmiColor = '#27AE60';
          }
        }

        return [
          {
            title: 'Вес',
            value: currentWeight,
            target: targetWeight,
            unit: 'кг',
            icon: Scale,
            color: weightColor,
            displayValue: `${currentWeight}`,
          },
          {
            title: 'За неделю',
            value: Math.abs(weeklyChange),
            target: 0.5,
            unit: 'кг',
            icon: weeklyIcon,
            color: weeklyColor,
            displayValue: weeklyChange === 0 ? '0.0' : `${weeklyPrefix}${Math.abs(weeklyChange)}`,
          },
          {
            title: 'Общее изменение',
            value: Math.abs(totalChange),
            target: 3,
            unit: 'кг',
            icon: Award,
            color: totalColor,
            displayValue: totalChange === 0 ? '0.0' : `${totalPrefix}${Math.abs(totalChange)}`,
          },
          {
            title: 'ИМТ',
            value: bmi,
            target: targetBMI,
            unit: '',
            icon: Activity,
            color: bmiColor,
            displayValue: `${bmi}`,
          }
        ];
      default:
        return [];
    }
  };

  const renderChart = () => {
    const data = getCurrentData();

    switch (activePeriod) {
      case 'day':
        const todayData = data[0] as DailyData;
        const todayMacros = [
          { name: 'Белки', value: todayData?.protein || 0, goal: 120, color: macroColors.protein },
          { name: 'Жиры', value: todayData?.fats || 0, goal: 70, color: macroColors.fats },
          { name: 'Углеводы', value: todayData?.carbs || 0, goal: 250, color: macroColors.carbs }
        ];

        return (
          <div className="flex justify-center px-4">
            <div className="w-full max-w-sm overflow-y-auto max-h-80 scroll-container">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={todayMacros}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke="var(--border)"
                    strokeOpacity={0.3}
                    horizontal={true}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                    tickMargin={8}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                    domain={[0, 300]}
                    ticks={[0, 50, 100, 150, 200, 250, 300]}
                    width={50}
                    tickFormatter={(value) => `${value}г`}
                    tickMargin={8}
                  />
                  {todayMacros.map((macro, index) => (
                    <ReferenceLine
                      key={index}
                      y={macro.goal}
                      stroke={macro.color}
                      strokeDasharray="4 6"
                      strokeOpacity={0.6}
                      strokeWidth={1.5}
                      label={{
                        value: `Цель: ${macro.goal}г`,
                        position: 'right',
                        style: {
                          fontSize: '10px',
                          fontWeight: '500',
                          fill: macro.color
                        }
                      }}
                    />
                  ))}
                  <Bar
                    dataKey="value"
                    radius={[8, 8, 0, 0]}
                  >
                    {todayMacros.map((macro, index) => (
                      <Cell key={`cell-${index}`} fill={macro.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        fill: 'var(--foreground)'
                      }}
                      formatter={(value: number) => `${value}г`}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'week':
        const weeklyMacros = [
          {
            name: 'Белки',
            value: Math.round(weeklyData.reduce((sum, day) => sum + day.protein, 0) / 7),
            goal: 120,
            color: macroColors.protein
          },
          {
            name: 'Жиры',
            value: Math.round(weeklyData.reduce((sum, day) => sum + day.fats, 0) / 7),
            goal: 70,
            color: macroColors.fats
          },
          {
            name: 'Углеводы',
            value: Math.round(weeklyData.reduce((sum, day) => sum + day.carbs, 0) / 7),
            goal: 250,
            color: macroColors.carbs
          }
        ];

        return (
          <div className="space-y-8">
            {/* БЖУ диаграмма */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-center">Средние БЖУ за неделю</h4>
              <div className="flex justify-center px-4">
                <div className="w-full max-w-sm overflow-y-auto max-h-80 scroll-container">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={weeklyMacros}
                      margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                      barCategoryGap="20%"
                    >
                      <CartesianGrid
                        strokeDasharray="2 4"
                        stroke="var(--border)"
                        strokeOpacity={0.3}
                        horizontal={true}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                        tickMargin={8}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                        domain={[0, 300]}
                        ticks={[0, 50, 100, 150, 200, 250, 300]}
                        width={50}
                        tickFormatter={(value) => `${value}г`}
                        tickMargin={8}
                      />
                      {weeklyMacros.map((macro, index) => (
                        <ReferenceLine
                          key={index}
                          y={macro.goal}
                          stroke={macro.color}
                          strokeDasharray="4 6"
                          strokeOpacity={0.6}
                          strokeWidth={1.5}
                          label={{
                            value: `Цель: ${macro.goal}г`,
                            position: 'right',
                            style: {
                              fontSize: '10px',
                              fontWeight: '500',
                              fill: macro.color
                            }
                          }}
                        />
                      ))}
                      <Bar
                        dataKey="value"
                        radius={[8, 8, 0, 0]}
                      >
                        {weeklyMacros.map((macro, index) => (
                          <Cell key={`cell-${index}`} fill={macro.color} />
                        ))}
                        <LabelList
                          dataKey="value"
                          position="top"
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            fill: 'var(--foreground)'
                          }}
                          formatter={(value: number) => `${value}г`}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Пунктирные линии показывают дневные цели БЖУ
              </p>
            </div>

            {/* Калории за неделю диаграмма */}
            <div>
              <h4 className="text-lg font-semibold mb-4 text-center">Калории за неделю</h4>
              <div className="flex justify-center px-4">
                <div className="w-full max-w-md">
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart
                      data={weeklyData}
                      margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                    >
                      {/* Минималистичная сетка */}
                      <CartesianGrid
                        strokeDasharray="2 4"
                        stroke="var(--border)"
                        strokeOpacity={0.3}
                        horizontal={true}
                        vertical={false}
                      />

                      <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                        tickMargin={8}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                        domain={[0, 'dataMax + 200']}
                        tickMargin={8}
                        width={50}
                        tickFormatter={(value) => `${value}`}
                      />

                      {/* Элегантная линия цели */}
                      <ReferenceLine
                        y={Number(targets?.calories) || 0}
                        stroke="#27AE60"
                        strokeDasharray="4 6"
                        strokeOpacity={0.6}
                        strokeWidth={1.5}
                        label={{
                          value: "Цель",
                          position: "right",
                          fontSize: 10,
                          fill: '#27AE60',
                          fontWeight: 500
                        }}
                      />

                      {/* Градиенты для области */}
                      <defs>
                        <linearGradient id="weekGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#7B7BA8" stopOpacity={0.4} />
                          <stop offset="50%" stopColor="#595880" stopOpacity={0.2} />
                          <stop offset="100%" stopColor="#595880" stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="weekLineGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#7B7BA8" />
                          <stop offset="100%" stopColor="#595880" />
                        </linearGradient>
                      </defs>

                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div style={{
                                background: 'rgba(42, 42, 42, 0.95)',
                                border: '1px solid rgba(123, 123, 168, 0.3)',
                                borderRadius: '12px',
                                padding: '10px 14px',
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                              }}>
                                <p style={{ margin: 0, fontWeight: 600, color: '#7B7BA8', fontSize: '12px' }}>
                                  {data.day}
                                </p>
                                <p style={{ margin: '4px 0 0', color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                                  {data.calories} ккал
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                        cursor={false}
                        wrapperStyle={{ pointerEvents: 'none' }}
                      />

                      <Area
                        type="monotone"
                        dataKey="calories"
                        stroke="url(#weekLineGradient)"
                        strokeWidth={3}
                        fill="url(#weekGradient)"
                        dot={{ fill: '#595880', stroke: '#fff', strokeWidth: 2, r: 5 }}
                        activeDot={{ fill: '#7B7BA8', stroke: '#fff', strokeWidth: 3, r: 7 }}
                        isAnimationActive={false}
                        connectNulls={true}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Пунктирная линия показывает дневную цель калорий
              </p>
            </div>
          </div>
        );

      case 'weight':
        // Если данных нет совсем - показываем заглушку или пустой график
        if (!weightData || weightData.length === 0) {
          return (
            <div className="flex justify-center px-4">
              <div className="w-full max-w-md h-[220px] flex items-center justify-center text-muted-foreground text-sm bg-section-bg/30 rounded-2xl border border-dashed border-border">
                Нет данных о весе
              </div>
            </div>
          );
        }

        const selectedWeightData = weightPoints === 'all' ? weightData : weightData.slice(-weightPoints);

        // Если после фильтрации по количеству данных нет - показываем заглушку
        if (!selectedWeightData.length) {
          return (
            <div className="flex justify-center px-4">
              <div className="w-full max-w-md h-[220px] flex items-center justify-center text-muted-foreground text-sm bg-section-bg/30 rounded-2xl border border-dashed border-border">
                Нет данных о весе
              </div>
            </div>
          );
        }

        // Преобразуем данные для равномерного распределения по X
        const processedWeightData = selectedWeightData.map((item, index) => ({
          ...item,
          index: index,
          displayDate: (() => {
            try {
              const date = new Date(item.date);
              if (isNaN(date.getTime())) return '';
              return `${date.getDate()}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
            } catch {
              return '';
            }
          })()
        })) as (WeightData & { index: number; displayDate: string })[];

        // Определяем диапазон для Y оси с целыми числами
        const weights = selectedWeightData.map(d => d.weight);
        const minWeight = weights.length > 0 ? Math.min(...weights) : 0;
        const maxWeight = weights.length > 0 ? Math.max(...weights) : 100;

        // Делаем отступы сверху и снизу
        const yAxisMin = Math.floor(minWeight - 1);
        const yAxisMax = Math.ceil(maxWeight + 1);

        // Создаем массив всех целых значений для Y оси
        const integerYTicks = [];
        for (let i = yAxisMin; i <= yAxisMax; i++) {
          integerYTicks.push(i);
        }

        // Создаем массив половинных тиков (0.5)
        const halfYTicks = [];
        for (let i = yAxisMin; i < yAxisMax; i++) {
          halfYTicks.push(i + 0.5);
        }

        return (
          <div>
            <div className="flex justify-center px-4 mb-3">
              <div className="period-selector-container">
                {weightPointOptions.map((opt) => (
                  <button
                    key={String(opt.id)}
                    onClick={() => setWeightPoints(opt.id)}
                    className={`period-selector-button ${weightPoints === opt.id ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-center px-2">
              <div className="w-full max-w-md">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart
                    data={processedWeightData}
                    margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="statsWeightGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7B7BA8" stopOpacity={0.4} />
                        <stop offset="50%" stopColor="#595880" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#595880" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="statsWeightLine" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#7B7BA8" />
                        <stop offset="100%" stopColor="#595880" />
                      </linearGradient>
                    </defs>

                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.1)"
                      horizontal={true}
                      vertical={false}
                    />

                    <XAxis
                      dataKey="displayDate"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                      tickMargin={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                      domain={[yAxisMin, yAxisMax]}
                      ticks={integerYTicks}
                      tickFormatter={(value) => `${value}кг`}
                      tickMargin={8}
                      width={50}
                    />

                    {/* Линия цели */}
                    <ReferenceLine
                      y={Number(((globalThis as any)._appUserData?.mini?.profile?.targetWeight) ?? 0) || 0}
                      stroke="#27AE60"
                      strokeDasharray="4 4"
                      strokeOpacity={0.8}
                      strokeWidth={2}
                    />

                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{
                              background: 'rgba(42, 42, 42, 0.95)',
                              border: '1px solid rgba(123, 123, 168, 0.3)',
                              borderRadius: '12px',
                              padding: '10px 14px',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                            }}>
                              <p style={{ margin: 0, fontWeight: 600, color: '#7B7BA8', fontSize: '12px' }}>
                                {data.displayDate}
                              </p>
                              <p style={{ margin: '4px 0 0', color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                                {data.weight} кг
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                      cursor={false}
                      wrapperStyle={{ pointerEvents: 'none' }}
                    />

                    <Area
                      type="monotone"
                      dataKey="weight"
                      stroke="url(#statsWeightLine)"
                      strokeWidth={3}
                      fill="url(#statsWeightGradient)"
                      dot={{ fill: '#595880', stroke: '#fff', strokeWidth: 2, r: 5 }}
                      activeDot={{ fill: '#7B7BA8', stroke: '#fff', strokeWidth: 3, r: 7 }}
                      isAnimationActive={false}
                      connectNulls={true}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const statsCards = getStatsCards();

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="premium-card p-4">
        <div className="period-selector-container">
          {periods.map((period) => (
            <button
              key={period.id}
              onClick={() => setActivePeriod(period.id)}
              className={`period-selector-button ${activePeriod === period.id ? 'active' : ''}`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        {statsCards.map((stat, _index) => (
          <div
            key={stat.title}
            className="premium-card p-4 premium-interaction"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20` }}
              >
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              <div className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-2xl font-bold" style={{ color: stat.color }}>
                {(stat as any).displayValue ?? stat.value}{stat.unit}
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Цель: {stat.target}{stat.unit}
                </span>
                <span
                  className="font-semibold"
                  style={{
                    color: stat.value >= stat.target ? '#27AE60' : '#F39C12'
                  }}
                >
                  {Math.round((stat.value / stat.target) * 100)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart Section */}
      <div className="premium-card p-6">
        {renderChart()}
      </div>
    </div>
  );
}

// ===== WEIGHT TRACKER COMPONENT =====
function WeightTracker({
  weightData,
  onAddWeight
}: {
  weightData: WeightData[];
  onAddWeight: (weight: number) => void
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempWeight, setTempWeight] = useState('');
  const [chartPoints, setChartPoints] = useState<5 | 10 | 30 | 'all'>(5);

  const chartPointOptions: Array<{ id: 5 | 10 | 30 | 'all'; label: string }> = [
    { id: 5, label: '5' },
    { id: 10, label: '10' },
    { id: 30, label: '30' },
    { id: 'all', label: 'Все' },
  ];

  // Берём реальные данные (без моков)
  // Берём реальные данные (без моков)
  const safeWeightData = Array.isArray(weightData) ? weightData : [];
  const currentWeight = safeWeightData.length > 0 ? safeWeightData[safeWeightData.length - 1].weight : 0;
  const previousWeight = safeWeightData.length > 1 ? safeWeightData[safeWeightData.length - 2].weight : currentWeight;
  const weightChange = currentWeight - previousWeight;
  const userGoal = (globalThis as any)._appUserData?.mini?.profile?.goal || 'maintain';
  const targetWeight = (globalThis as any)._appUserData?.mini?.profile?.targetWeight ?? 0;
  // Если цель "поддержание" - целевой вес = текущий вес
  const goalWeight = userGoal === 'maintain' ? currentWeight : targetWeight;

  // Вычисляем ИМТ (рост из профиля в метрах)
  const height = (((globalThis as any)._appUserData?.mini?.profile?.height) ?? 0) / 100;
  const currentBMI = height > 0 ? currentWeight / (height * height) : 0;

  // Определяем категорию ИМТ
  const getBMICategory = (bmi: number) => {
    if (bmi < 18.5) return { text: 'Недовес', color: 'text-warning' };
    if (bmi < 25) return { text: 'Норма', color: 'text-success' };
    if (bmi < 30) return { text: 'Избыток', color: 'text-warning' };
    return { text: 'Ожирение', color: 'text-destructive' };
  };

  const bmiCategory = getBMICategory(currentBMI);
  const weightTrend = weightChange > 0.1 ? 'up' : weightChange < -0.1 ? 'down' : 'stable';
  const remainingToGoal = Math.abs(currentWeight - goalWeight);
  const isGoalReached = userGoal === 'maintain' || Math.abs(currentWeight - goalWeight) <= 0.5;

  // Обработчики
  const handleEditClick = () => {
    setTempWeight(currentWeight.toString());
    setIsEditing(true);
  };

  const handleSave = () => {
    const newWeight = parseFloat(tempWeight);
    console.log('💾 Saving weight:', { tempWeight, newWeight, isValid: newWeight && newWeight > 0 && newWeight < 300 });
    if (newWeight && newWeight > 0 && newWeight < 300) {
      onAddWeight(newWeight);
      setIsEditing(false);
      setTempWeight('');
    } else {
      console.error('❌ Invalid weight value:', tempWeight);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setTempWeight('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="space-y-4">
      {/* Основная карточка веса */}
      <div className="premium-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Scale className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">Мой вес</h3>
              <p className="text-sm text-muted-foreground">
                Сегодня, {new Date().toLocaleDateString('ru-RU')}
              </p>
            </div>
          </div>

          <button
            onClick={handleEditClick}
            className="w-11 h-11 rounded-2xl bg-section-bg hover:bg-hover flex items-center justify-center transition-all duration-200"
            disabled={isEditing}
          >
            <Edit3 className="w-5 h-5 text-primary" />
          </button>
        </div>

        {/* Вес и редактирование */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Текущий вес */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {isEditing ? (
                <motion.div
                  key="editing"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex items-center gap-4"
                >
                  <div className="flex-1">
                    <div className="bg-section-bg/50 rounded-2xl p-4 border-2 border-primary/10 focus-within:border-primary/30 transition-all duration-200">
                      <input
                        type="number"
                        value={tempWeight}
                        onChange={(e) => setTempWeight(e.target.value)}
                        onKeyDown={handleKeyPress}
                        className="w-full text-4xl font-bold bg-transparent border-none outline-none text-foreground placeholder-placeholder"
                        placeholder="0.0"
                        step="0.1"
                        min="1"
                        max="300"
                        autoFocus
                      />
                      <span className="text-2xl font-medium text-muted-foreground">кг</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      className="w-12 h-12 rounded-2xl bg-success text-success-foreground flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-success/90"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="w-12 h-12 rounded-2xl bg-section-bg text-muted-foreground flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-hover"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="display"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-4xl font-bold text-foreground">{currentWeight}</span>
                    <span className="text-2xl font-medium text-muted-foreground">кг</span>
                  </div>

                  {/* Изменение веса */}
                  <div className="flex items-center gap-2">
                    {weightTrend === 'up' ? (
                      <TrendingUp className="w-4 h-4 text-warning" />
                    ) : weightTrend === 'down' ? (
                      <TrendingDown className="w-4 h-4 text-success" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-muted-foreground/20" />
                    )}
                    <span className={`text-sm font-medium ${weightTrend === 'up' ? 'text-warning' :
                      weightTrend === 'down' ? 'text-success' :
                        'text-muted-foreground'
                      }`}>
                      {weightChange === 0 ? 'Без изменений' :
                        `${weightChange > 0 ? '+' : ''}${weightChange.toFixed(1)} кг за неделю`}
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Статистика */}
          <div className="space-y-4">
            {/* Цель */}
            <div className="weight-info-block rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className={`w-4 h-4 ${isGoalReached ? 'text-success' : 'text-warning'}`} />
                <span className="text-sm text-muted-foreground">
                  {userGoal === 'maintain' ? 'Цель' : 'До цели'}
                </span>
              </div>
              {userGoal === 'maintain' ? (
                <div>
                  <div className="text-2xl font-semibold text-success">Поддержание</div>
                  <div className="text-xs text-success/80 font-medium">Текущий вес: {currentWeight} кг</div>
                </div>
              ) : isGoalReached ? (
                <div>
                  <div className="text-2xl font-semibold text-success">Достигнута!</div>
                  <div className="text-xs text-success/80 font-medium">Цель: {goalWeight} кг</div>
                </div>
              ) : (
                <div>
                  <div className="text-2xl font-semibold text-foreground">{remainingToGoal.toFixed(1)} кг</div>
                  <div className="text-xs text-muted-foreground">Цель: {goalWeight} кг</div>
                </div>
              )}
            </div>

            {/* ИМТ */}
            <div className="weight-info-block rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-accent" />
                <span className="text-sm text-muted-foreground">ИМТ</span>
              </div>
              <div className="text-2xl font-semibold text-foreground">{currentBMI.toFixed(1)}</div>
              <div className={`text-xs font-medium ${bmiCategory.color}`}>
                {bmiCategory.text}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* График динамики - УБРАЛ ИКОНКУ Calendar! */}
      <div className="premium-card p-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-info" />
            </div>
            <div>
              <h4 className="text-lg font-semibold">Динамика веса</h4>
              <p className="text-sm text-muted-foreground">Последние измерения</p>
            </div>
          </div>

          <div className="period-selector-container">
            {chartPointOptions.map((opt) => (
              <button
                key={String(opt.id)}
                onClick={() => setChartPoints(opt.id)}
                className={`period-selector-button ${chartPoints === opt.id ? 'active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-center px-2">
          <div className="w-full max-w-md">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={(() => {
                  const data = chartPoints === 'all' ? safeWeightData : safeWeightData.slice(-chartPoints);
                  return data.map((item: any, index: number) => ({
                    ...item,
                    index,
                    displayDate: (() => {
                      const date = new Date(item.date);
                      return `${date.getDate()}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                    })()
                  }));
                })()}
                margin={{ top: 10, right: 40, left: 10, bottom: 10 }}
              >
                <defs>
                  <linearGradient id="weightLineGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#7B7BA8" />
                    <stop offset="100%" stopColor="#595880" />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.1)"
                  horizontal={true}
                  vertical={false}
                />

                <XAxis
                  dataKey="displayDate"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                  tickMargin={8}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontWeight: 500 }}
                  tickFormatter={(value) => `${value}кг`}
                  tickMargin={8}
                  width={50}
                  domain={[(dataMin: number) => Math.floor(dataMin - 2), (dataMax: number) => Math.ceil(dataMax + 2)]}
                />

                {/* Линия цели */}
                <ReferenceLine
                  y={72}
                  stroke="#27AE60"
                  strokeDasharray="4 4"
                  strokeOpacity={0.8}
                  strokeWidth={2}
                />

                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const date = new Date(data.date);
                      const formattedDate = `${date.getDate()}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                      return (
                        <div style={{
                          background: 'rgba(42, 42, 42, 0.95)',
                          border: '1px solid rgba(123, 123, 168, 0.3)',
                          borderRadius: '12px',
                          padding: '10px 14px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        }}>
                          <p style={{ margin: 0, fontWeight: 600, color: '#7B7BA8', fontSize: '12px' }}>
                            {formattedDate}
                          </p>
                          <p style={{ margin: '4px 0 0', color: '#fff', fontSize: '16px', fontWeight: 600 }}>
                            {data.weight} кг
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                  cursor={false}
                  wrapperStyle={{ pointerEvents: 'none' }}
                />

                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="url(#weightLineGradient)"
                  strokeWidth={3}
                  dot={{ fill: '#595880', stroke: '#fff', strokeWidth: 2, r: 5 }}
                  activeDot={{ fill: '#7B7BA8', stroke: '#fff', strokeWidth: 3, r: 7 }}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            Зелёная пунктирная линия показывает целевой вес
          </p>
        </div>
      </div>
    </div>
  );
}

// ===== CONGRATULATIONS MODAL COMPONENT =====
interface CongratulationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateGoal?: () => void;
  achievement: {
    type: 'weight_goal' | 'streak' | 'calorie_target' | 'first_meal';
    title: string;
    message: string;
    value?: string | number;
  } | null;
}

function CongratulationsModal({ isOpen, onClose, onUpdateGoal, achievement }: CongratulationsModalProps) {
  const [confetti, setConfetti] = useState<Array<{ id: number; x: number; delay: number; color: string }>>([]);

  useEffect(() => {
    if (isOpen) {
      // Generate confetti particles
      const particles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        color: ['#7B7BA8', '#595880', '#27AE60', '#F39C12', '#E74C3C', '#3498DB'][Math.floor(Math.random() * 6)]
      }));
      setConfetti(particles);
    }
  }, [isOpen]);

  if (!isOpen || !achievement) return null;

  const getIcon = () => {
    switch (achievement.type) {
      case 'weight_goal': return '🎯';
      case 'streak': return '🔥';
      case 'calorie_target': return '✅';
      case 'first_meal': return '🍽️';
      default: return '🎉';
    }
  };

  const isWeightGoal = achievement.type === 'weight_goal';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {confetti.map((particle) => (
          <div
            key={particle.id}
            className="absolute w-3 h-3 rounded-full animate-confetti"
            style={{
              left: `${particle.x}%`,
              backgroundColor: particle.color,
              animationDelay: `${particle.delay}s`,
              top: '-20px'
            }}
          />
        ))}
      </div>

      {/* Modal */}
      <div
        className="relative rounded-3xl p-8 mx-4 max-w-sm w-full shadow-2xl transform animate-bounce-in"
        style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--section-bg)' }}
        >
          <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
        </button>

        {/* Content */}
        <div className="text-center">
          <div className="text-6xl mb-4">{getIcon()}</div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
            {achievement.title}
          </h2>
          <p className="mb-4" style={{ color: 'var(--muted-foreground)' }}>
            {achievement.message}
          </p>
          {achievement.value && (
            <div className="inline-block px-4 py-2 rounded-xl mb-6" style={{ backgroundColor: 'rgba(89, 88, 128, 0.15)' }}>
              <span className="text-xl font-bold" style={{ color: 'var(--primary)' }}>{achievement.value}</span>
            </div>
          )}

          {isWeightGoal && onUpdateGoal ? (
            <div className="space-y-3">
              <button
                onClick={() => {
                  onClose();
                  onUpdateGoal();
                }}
                className="w-full py-3 rounded-xl font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
              >
                ⚙️ Обновить цель
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl font-semibold"
                style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
              >
                Супер! 🎉
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
            >
              Отлично! 🎉
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== TIPS MODAL (MINI APP ADVICE) =====
interface TipsModalProps {
  isOpen: boolean;
  loading: boolean;
  title: string;
  tips: string[];
  onClose: () => void;
}

function TipsModal({ isOpen, loading, title, tips, onClose }: TipsModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative rounded-3xl p-6 mx-4 max-w-sm w-full shadow-2xl"
        style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--section-bg)' }}
        >
          <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(243, 156, 18, 0.15)' }}
          >
            <Lightbulb className="w-6 h-6" style={{ color: 'var(--warning)' }} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>
              {title}
            </h2>
            <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Обновляется раз в 3 часа или после нового приёма
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Собираю подсказки…
          </div>
        ) : (
          <div className="space-y-3">
            {tips.map((t, idx) => (
              <div key={idx} className="flex gap-3 items-start">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--section-bg)' }}
                >
                  <Check className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                </div>
                <div className="text-sm" style={{ color: 'var(--foreground)' }}>
                  {t}
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full mt-6 py-3 rounded-xl font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
        >
          Понял!
        </button>
      </div>
    </div>
  );
}

// ===== ACTIVITY PAGE COMPONENT =====
type ActivityType = 'running' | 'walking' | 'cycling' | 'swimming' | 'strength' | 'yoga' | 'sports' | 'other';
type ActivityIntensity = 'low' | 'medium' | 'high';
type CaloriesMode = 'add_to_norm' | 'show_separate';

interface ActivityItem {
  id: string;
  type: ActivityType;
  name?: string;
  durationMinutes: number;
  intensity: ActivityIntensity;
  caloriesBurned: number;
  recordedAt: string;
  createdAt: string;
}

interface ActivityPageProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const ACTIVITY_TYPES: { id: ActivityType; label: string; icon: any; color: string }[] = [
  { id: 'running', label: 'Бег', icon: Activity, color: '#E74C3C' },
  { id: 'walking', label: 'Ходьба', icon: Footprints, color: '#27AE60' },
  { id: 'cycling', label: 'Велосипед', icon: Bike, color: '#3498DB' },
  { id: 'swimming', label: 'Плавание', icon: Waves, color: '#1ABC9C' },
  { id: 'strength', label: 'Силовая', icon: Dumbbell, color: '#9B59B6' },
  { id: 'yoga', label: 'Йога', icon: Heart, color: '#F39C12' },
  { id: 'sports', label: 'Спорт / HIIT', icon: Flame, color: '#E67E22' },
  { id: 'other', label: 'Другое', icon: Activity, color: '#7B7BA8' },
];

const INTENSITY_OPTIONS: { id: ActivityIntensity; label: string; multiplier: number }[] = [
  { id: 'low', label: 'Лёгкая', multiplier: 0.7 },
  { id: 'medium', label: 'Средняя', multiplier: 1.0 },
  { id: 'high', label: 'Высокая', multiplier: 1.3 },
];

const PRESET_CALS_PER_MIN: Record<ActivityType, number> = {
  running: 10,
  cycling: 8,
  swimming: 9,
  strength: 6,
  yoga: 4,
  walking: 5,
  sports: 8,
  other: 6,
};

function estimatePresetCalories(type: ActivityType, minutes: number, intensity: ActivityIntensity): number {
  const base = PRESET_CALS_PER_MIN[type] || 6;
  const mult = INTENSITY_OPTIONS.find((x) => x.id === intensity)?.multiplier ?? 1;
  return Math.round(base * Math.max(1, minutes) * mult);
}

type ActivityPreset = {
  id: string;
  label: string;
  type: ActivityType;
  name?: string;
  icon: any;
  color: string;
  defaultMinutes: number;
  defaultIntensity: ActivityIntensity;
};

const ACTIVITY_PRESETS: ActivityPreset[] = [
  { id: 'walk', label: 'Ходьба', type: 'walking', icon: Footprints, color: '#27AE60', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'run', label: 'Бег', type: 'running', icon: Activity, color: '#E74C3C', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'bike', label: 'Велоспорт', type: 'cycling', icon: Bike, color: '#3498DB', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'elliptical', label: 'Эллипсоид', type: 'other', name: 'Эллипсоид', icon: Activity, color: '#16A085', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'strength', label: 'Силовая', type: 'strength', icon: Dumbbell, color: '#9B59B6', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'swim', label: 'Плавание', type: 'swimming', icon: Waves, color: '#1ABC9C', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'yoga', label: 'Йога / растяжка', type: 'yoga', icon: Heart, color: '#F39C12', defaultMinutes: 30, defaultIntensity: 'low' },
  { id: 'hiit', label: 'HIIT', type: 'sports', name: 'HIIT', icon: Flame, color: '#E67E22', defaultMinutes: 20, defaultIntensity: 'high' },
  { id: 'football', label: 'Футбол', type: 'sports', name: 'Футбол', icon: Activity, color: '#2ECC71', defaultMinutes: 60, defaultIntensity: 'high' },
  { id: 'basketball', label: 'Баскетбол', type: 'sports', name: 'Баскетбол', icon: Activity, color: '#D35400', defaultMinutes: 45, defaultIntensity: 'high' },
  { id: 'dance', label: 'Танцы', type: 'sports', name: 'Танцы', icon: Activity, color: '#8E44AD', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'stairs', label: 'Лестница', type: 'other', name: 'Подъём по лестнице', icon: TrendingUp, color: '#C0392B', defaultMinutes: 20, defaultIntensity: 'high' },
  { id: 'rowing', label: 'Гребля', type: 'other', name: 'Гребля', icon: Activity, color: '#2980B9', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'other', label: 'Другое', type: 'other', icon: Activity, color: '#7B7BA8', defaultMinutes: 30, defaultIntensity: 'medium' },
];

function ActivityPage({ selectedDate }: ActivityPageProps) {
  const api = useApi();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalCalories: 0, totalDuration: 0, count: 0 });
  const [caloriesMode, setCaloriesMode] = useState<CaloriesMode>('show_separate');
  const [showAddModal, setShowAddModal] = useState(false);
  const [freeText, setFreeText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const aiReqSeqRef = useRef(0);
  const userEditedMinutesRef = useRef(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [newActivity, setNewActivity] = useState<{
    type: ActivityType;
    name?: string;
    durationMinutes: string;
    intensity: ActivityIntensity;
    calculatedCalories: number;
    met?: number;
    confidence?: number;
    fromAi?: boolean;
  }>({
    type: 'running',
    name: undefined,
    durationMinutes: '30',
    intensity: 'medium',
    calculatedCalories: 0,
    met: undefined,
    confidence: undefined,
    fromAi: false,
  });
  const [saving, setSaving] = useState(false);

  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const userWeightKg = useMemo(() => {
    const p = (globalThis as any)._appUserData?.mini?.profile;
    const w = Number(p?.weight ?? p?.currentWeight);
    return Number.isFinite(w) && w > 0 ? w : 70;
  }, []);

  const kcalFromMet = useCallback((met: number, minutes: number) => {
    const m = Math.max(1, Math.min(25, Number(met) || 0));
    const w = Math.max(30, Math.min(250, Number(userWeightKg) || 70));
    const min = Math.max(1, Math.min(600, Math.round(Number(minutes) || 0)));
    return Math.round((m * 3.5 * w) / 200 * min);
  }, [userWeightKg]);

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const dateStr = formatLocalDate(selectedDate);

      const data = await api.getActivities(dateStr);

      setActivities(data.activities || []);
      setSummary(data.summary || { totalCalories: 0, totalDuration: 0, count: 0 });
      setCaloriesMode(data.caloriesMode || 'show_separate');
    } catch (e) {
      console.error('Failed to load activities:', e);
    } finally {
      setLoading(false);
    }
  }, [api, selectedDate]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const runAiEstimate = useCallback(async () => {
    const text = freeText.trim();
    if (!text) return;

    const seq = ++aiReqSeqRef.current;
    setAiLoading(true);
    setAiError(null);
    setSelectedPresetId(null);
    userEditedMinutesRef.current = false;

    try {
      const est = await api.estimateActivityFromText(text);
      if (seq !== aiReqSeqRef.current) return;

      setNewActivity((prev) => {
        const durationStr = String(est.durationMinutes);
        const duration = parseInt(durationStr) || est.durationMinutes;
        const calories = Number(est.met) > 0 ? kcalFromMet(est.met, duration) : est.caloriesBurned;
        return {
          ...prev,
          type: est.type,
          name: est.name,
          intensity: est.intensity,
          durationMinutes: durationStr,
          met: est.met,
          confidence: est.confidence,
          calculatedCalories: calories,
          fromAi: true,
        };
      });
    } catch (e: any) {
      if (seq !== aiReqSeqRef.current) return;
      const msg = String(e?.message || 'Не удалось распознать активность');
      setAiError(msg.includes('404')
        ? 'AI-эндпоинт недоступен (404). Нужен рестарт backend/деплой.'
        : msg);
    } finally {
      if (seq === aiReqSeqRef.current) setAiLoading(false);
    }
  }, [api, freeText, kcalFromMet]);

  const openAddModal = () => {
    aiReqSeqRef.current += 1;
    userEditedMinutesRef.current = false;
    setFreeText('');
    setAiError(null);
    setAiLoading(false);
    setSelectedPresetId(null);
    setNewActivity({
      type: 'running',
      name: undefined,
      durationMinutes: '30',
      intensity: 'medium',
      calculatedCalories: 0,
      met: undefined,
      confidence: undefined,
      fromAi: false,
    });
    setShowAddModal(true);
  };

  useEffect(() => {
    const duration = parseInt(newActivity.durationMinutes) || 0;
    if (!showAddModal) return;
    if (!(duration > 0)) return;

    // If we have MET from AI, prefer deterministic local calculation to keep preview in sync.
    if (newActivity.fromAi && Number(newActivity.met) > 0) {
      const calories = kcalFromMet(Number(newActivity.met), duration);
      setNewActivity(prev => (prev.calculatedCalories === calories ? prev : { ...prev, calculatedCalories: calories }));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const calories = await api.calculateActivityCalories(newActivity.type, duration, newActivity.intensity);
        if (cancelled) return;
        setNewActivity(prev => (prev.calculatedCalories === calories ? prev : { ...prev, calculatedCalories: calories }));
      } catch (e) {
        console.error('Failed to calculate calories:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [newActivity.type, newActivity.durationMinutes, newActivity.intensity, newActivity.fromAi, newActivity.met, showAddModal, api, kcalFromMet]);

  const handleAddActivity = async () => {
    setSaving(true);
    try {
      const duration = parseInt(newActivity.durationMinutes) || 30;
      await api.createActivity({
        type: newActivity.type,
        name: newActivity.name,
        durationMinutes: duration,
        intensity: newActivity.intensity,
        caloriesBurned: newActivity.calculatedCalories || undefined,
        recordedAt: formatLocalDate(selectedDate),
      });
      setShowAddModal(false);
      setNewActivity({
        type: 'running',
        name: undefined,
        durationMinutes: '30',
        intensity: 'medium',
        calculatedCalories: 0,
        met: undefined,
        confidence: undefined,
        fromAi: false,
      });
      loadActivities();
      window.dispatchEvent(new CustomEvent('activities-updated'));
    } catch (e) {
      console.error('Failed to create activity:', e);
      alert('Не удалось добавить активность');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteActivity = async (id: string) => {
    if (!confirm('Удалить активность?')) return;
    try {
      await api.deleteActivity(id);
      loadActivities();
      window.dispatchEvent(new CustomEvent('activities-updated'));
    } catch (e) {
      console.error('Failed to delete activity:', e);
      alert('Не удалось удалить активность');
    }
  };

  const handleCaloriesModeChange = async (mode: CaloriesMode) => {
    try {
      await api.setCaloriesMode(mode);
      setCaloriesMode(mode);
      window.dispatchEvent(new CustomEvent('activities-updated'));
    } catch (e) {
      console.error('Failed to set calories mode:', e);
    }
  };

  const getActivityTypeInfo = (type: ActivityType) => {
    return ACTIVITY_TYPES.find(t => t.id === type) || ACTIVITY_TYPES[7];
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}ч ${mins}м` : `${hours}ч`;
  };

  const dateLabel = selectedDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

  return (
    <div className="space-y-6">
      {/* Сводка за день */}
      <div className="premium-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)' }}
            >
              <Activity className="w-6 h-6 text-white flex-shrink-0" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">Активность</h3>
              <p className="text-muted-foreground text-sm">{dateLabel}</p>
            </div>
          </div>
          <button
            onClick={openAddModal}
            className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-150 hover:scale-105 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
          >
            <Plus className="w-6 h-6 text-white flex-shrink-0" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="section-card p-4 text-center">
            <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: '#27AE6020' }}>
              <Flame className="w-5 h-5" style={{ color: '#27AE60' }} />
            </div>
            <div className="text-2xl font-bold text-foreground">{summary.totalCalories}</div>
            <div className="text-xs text-muted-foreground">ккал сожжено</div>
          </div>
          <div className="section-card p-4 text-center">
            <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: '#3498DB20' }}>
              <Timer className="w-5 h-5" style={{ color: '#3498DB' }} />
            </div>
            <div className="text-2xl font-bold text-foreground">{formatDuration(summary.totalDuration)}</div>
            <div className="text-xs text-muted-foreground">общее время</div>
          </div>
          <div className="section-card p-4 text-center">
            <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: '#9B59B620' }}>
              <Activity className="w-5 h-5" style={{ color: '#9B59B6' }} />
            </div>
            <div className="text-2xl font-bold text-foreground">{summary.count}</div>
            <div className="text-xs text-muted-foreground">тренировок</div>
          </div>
        </div>
      </div>

      {/* Режим учёта калорий */}
      <div className="premium-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm font-medium">Учёт сожжённых калорий</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleCaloriesModeChange('add_to_norm')}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: caloriesMode === 'add_to_norm' ? 'var(--primary)' : 'var(--section-bg)',
                color: caloriesMode === 'add_to_norm' ? 'white' : 'var(--muted-foreground)'
              }}
            >
              + к норме
            </button>
            <button
              onClick={() => handleCaloriesModeChange('show_separate')}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                backgroundColor: caloriesMode === 'show_separate' ? 'var(--primary)' : 'var(--section-bg)',
                color: caloriesMode === 'show_separate' ? 'white' : 'var(--muted-foreground)'
              }}
            >
              Отдельно
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {caloriesMode === 'add_to_norm'
            ? 'Сожжённые калории добавляются к дневной норме'
            : 'Сожжённые калории показываются отдельно'}
        </p>
      </div>

      {/* Список активностей */}
      <div className="premium-card p-6">
        <h4 className="font-semibold mb-4">Тренировки за день</h4>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Загрузка...</div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8">
            <Activity className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-muted-foreground">Нет активностей</p>
            <p className="text-xs text-muted-foreground mt-1">Нажмите + чтобы добавить</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const typeInfo = getActivityTypeInfo(activity.type);
              const IconComponent = typeInfo.icon;
              return (
                <motion.div
                  key={activity.id}
                  className="section-card p-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${typeInfo.color}20` }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: typeInfo.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{activity.name || typeInfo.label}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{formatDuration(activity.durationMinutes)}</span>
                        <span>•</span>
                        <span>{INTENSITY_OPTIONS.find(i => i.id === activity.intensity)?.label || 'Средняя'}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold text-lg" style={{ color: '#27AE60' }}>
                        -{activity.caloriesBurned}
                      </div>
                      <div className="text-xs text-muted-foreground">ккал</div>
                    </div>
                    <button
                      onClick={() => handleDeleteActivity(activity.id)}
                      className="w-8 h-8 rounded-full flex items-center justify-center bg-destructive/10 hover:bg-destructive/20 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Модалка добавления активности */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAddModal(false)} />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg rounded-t-3xl p-4 pb-24 max-h-[85vh] overflow-y-auto"
              style={{ backgroundColor: 'var(--card)', marginBottom: '70px' }}
            >
              <div className="w-10 h-1 bg-muted-foreground/20 rounded-full mx-auto mb-3" />

              {/* Кнопка закрытия */}
              <button
                onClick={() => setShowAddModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--section-bg)' }}
              >
                <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>

              <h3 className="text-lg font-semibold mb-4">Трекер активности</h3>

              {/* Free-form input */}
              <div className="mb-3">
                <label className="text-xs text-muted-foreground mb-2 block">Опишите активность</label>
                <textarea
                  value={freeText}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFreeText(v);
                    // Any change invalidates previous AI estimate
                    aiReqSeqRef.current += 1;
                    setAiLoading(false);
                    setAiError(null);
                    setSelectedPresetId(null);
                    setNewActivity((prev) => ({
                      ...prev,
                      fromAi: false,
                      met: undefined,
                      confidence: undefined,
                    }));
                  }}
                  rows={2}
                  className="w-full p-3 rounded-xl text-sm focus:outline-none resize-none"
                  style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
                  placeholder="Например: 45 мин велоспорт, 30 минут бег, силовая 50 мин"
                />
                <div className="mt-1 text-[11px] flex items-center justify-between" style={{ color: 'var(--muted-foreground)' }}>
                  <span>{aiLoading ? 'Распознаю…' : aiError ? aiError : newActivity.fromAi ? `Распознано: ${newActivity.name || newActivity.type}` : ' '}</span>
                  {newActivity.fromAi && typeof newActivity.confidence === 'number' && (
                    <span>уверенность {newActivity.confidence}%</span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={runAiEstimate}
                  disabled={aiLoading || !freeText.trim()}
                  className="w-full mt-2 py-3 rounded-xl font-semibold text-sm text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
                >
                  {aiLoading ? 'Распознаю…' : 'Распознать (AI)'}
                </button>
              </div>

              {/* Minutes + calories */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Минуты</label>
                  <input
                    type="number"
                    value={newActivity.durationMinutes}
                    onChange={(e) => {
                      userEditedMinutesRef.current = true;
                      setNewActivity((prev) => ({ ...prev, durationMinutes: e.target.value }));
                    }}
                    className="w-full p-3 rounded-xl text-center text-xl font-bold focus:outline-none"
                    style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
                    placeholder="30"
                    min="1"
                    max="600"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Сожжено</label>
                  <div className="w-full p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(39, 174, 96, 0.1)' }}>
                    <div className="text-xl font-bold" style={{ color: '#27AE60' }}>
                      ~{newActivity.calculatedCalories} ккал
                    </div>
                    <div className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                      {newActivity.intensity === 'low' ? 'лёгкая' : newActivity.intensity === 'high' ? 'высокая' : 'средняя'} нагрузка
                    </div>
                  </div>
                </div>
              </div>

              {/* Main action */}
              <div className="mb-4">
                <button
                  onClick={handleAddActivity}
                  disabled={saving || !newActivity.durationMinutes || (!!freeText.trim() && !newActivity.fromAi) || !!aiError}
                  className="w-full py-3 rounded-xl font-semibold text-sm text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)' }}
                >
                  {saving ? '...' : 'Добавить активность'}
                </button>
                {(selectedPresetId || newActivity.fromAi) && (
                  <div className="mt-2 text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                    Выбрано: <span style={{ color: 'var(--foreground)' }}>{newActivity.name || ACTIVITY_TYPES.find(t => t.id === newActivity.type)?.label || newActivity.type}</span>
                  </div>
                )}
              </div>

              {/* Presets */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-muted-foreground block">или выберите из списка</label>
                  <button
                    onClick={() => {
                      setFreeText('');
                      userEditedMinutesRef.current = false;
                      setAiError(null);
                    }}
                    className="text-[11px]"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    очистить ввод
                  </button>
                </div>

                <div className="space-y-2">
                  {ACTIVITY_PRESETS.map((p) => {
                    const Icon = p.icon;
                    const kcal = estimatePresetCalories(p.type, p.defaultMinutes, p.defaultIntensity);
                    const selected = selectedPresetId === p.id;
                    return (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-3 rounded-xl"
                        style={{
                          backgroundColor: selected ? `${p.color}18` : 'var(--section-bg)',
                          border: selected ? `2px solid ${p.color}` : '2px solid transparent',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            aiReqSeqRef.current += 1;
                            userEditedMinutesRef.current = false;
                            setFreeText('');
                            setAiError(null);
                            setSelectedPresetId(p.id);
                            setNewActivity({
                              type: p.type,
                              name: p.name ?? p.label,
                              durationMinutes: String(p.defaultMinutes),
                              intensity: p.defaultIntensity,
                              calculatedCalories: kcal,
                              met: undefined,
                              confidence: undefined,
                              fromAi: false,
                            });
                          }}
                          className="flex items-center gap-3 min-w-0"
                        >
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${p.color}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: p.color }} />
                          </div>
                          <div className="min-w-0 text-left">
                            <div className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{p.label}</div>
                            <div className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                              {p.defaultMinutes} мин • ~{kcal} ккал
                            </div>
                          </div>
                        </button>

                        <button
                          type="button"
                          disabled={saving}
                          onClick={async () => {
                            setSaving(true);
                            try {
                              await api.createActivity({
                                type: p.type,
                                name: p.name ?? p.label,
                                durationMinutes: p.defaultMinutes,
                                intensity: p.defaultIntensity,
                                caloriesBurned: kcal,
                                recordedAt: formatLocalDate(selectedDate),
                              });
                              setShowAddModal(false);
                              loadActivities();
                              window.dispatchEvent(new CustomEvent('activities-updated'));
                            } catch (e) {
                              console.error('Failed to create activity:', e);
                              alert('Не удалось добавить активность');
                            } finally {
                              setSaving(false);
                            }
                          }}
                          className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-150 hover:scale-105 active:scale-95 disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}
                        >
                          <Plus className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={() => setShowAddModal(false)}
                className="w-full py-3 rounded-xl font-semibold text-sm"
                style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
              >
                Закрыть
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ===== SETTINGS SCREEN COMPONENT =====
interface SettingsScreenProps {
  onBack: () => void;
  profile: any;
  onSave: (data: any) => Promise<void>;
}

function SettingsScreen({ onBack, profile, onSave }: SettingsScreenProps) {
  const [saving, setSaving] = useState(false);
  const [editingBirthDate, setEditingBirthDate] = useState(false);

  // Calculate birth year from age
  const currentYear = new Date().getFullYear();
  const defaultBirthDate = profile?.birthDate || `${currentYear - (profile?.age || 25)}-01-01`;

  const [formData, setFormData] = useState({
    gender: profile?.gender || 'male',
    birthDate: defaultBirthDate,
    height: profile?.height || 170,
    weight: profile?.currentWeight || 70,
    activityLevel: profile?.activityLevel || 'moderate',
    goal: profile?.goal || 'maintain',
    targetWeight: profile?.goalWeight || 70,
    pace: profile?.pace || 'balanced',
    // КБЖУ
    customKBJU: profile?.customKBJU?.enabled || false,
    calories: profile?.customKBJU?.calories || profile?.tdee || 2000,
    protein: profile?.customKBJU?.protein || profile?.protein || 120,
    carbs: profile?.customKBJU?.carbs || profile?.carbs || 250,
    fats: profile?.customKBJU?.fats || profile?.fats || 70,
  });

  // Calculate age from birth date
  const calculateAge = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const buildPayload = () => ({
    gender: formData.gender,
    birthDate: formData.birthDate,
    age: calculateAge(formData.birthDate),
    height: Number(formData.height),
    weight: Number(formData.weight),
    activityLevel: formData.activityLevel,
    goal: formData.goal,
    targetWeight: Number(formData.targetWeight),
    pace: formData.pace,
    customKBJU: formData.customKBJU
      ? {
        enabled: true,
        calories: Number(formData.calories),
        protein: Number(formData.protein),
        carbs: Number(formData.carbs),
        fats: Number(formData.fats),
      }
      : { enabled: false },
  });

  const initialPayloadRef = useRef<string>(
    JSON.stringify({
      gender: profile?.gender || 'male',
      birthDate: defaultBirthDate,
      age: calculateAge(defaultBirthDate),
      height: Number(profile?.height || 170),
      weight: Number(profile?.currentWeight || 70),
      activityLevel: profile?.activityLevel || 'moderate',
      goal: profile?.goal || 'maintain',
      targetWeight: Number(profile?.goalWeight || 70),
      pace: profile?.pace || 'balanced',
      customKBJU: profile?.customKBJU?.enabled
        ? {
          enabled: true,
          calories: Number(profile?.customKBJU?.calories || profile?.tdee || 2000),
          protein: Number(profile?.customKBJU?.protein || profile?.protein || 120),
          carbs: Number(profile?.customKBJU?.carbs || profile?.carbs || 250),
          fats: Number(profile?.customKBJU?.fats || profile?.fats || 70),
        }
        : { enabled: false },
    })
  );

  // КБЖУ auto-recalculation
  const recalculateFromCalories = (newCalories: number) => {
    // Standard distribution: 30% protein, 25% fats, 45% carbs
    const protein = Math.round((newCalories * 0.30) / 4); // 4 kcal per gram
    const fats = Math.round((newCalories * 0.25) / 9); // 9 kcal per gram
    const carbs = Math.round((newCalories * 0.45) / 4); // 4 kcal per gram
    return { protein, fats, carbs };
  };

  const recalculateFromMacro = (changedMacro: 'protein' | 'fats' | 'carbs', newValue: number) => {
    const currentCalories = Number(formData.calories);
    let protein = Number(formData.protein);
    let fats = Number(formData.fats);
    let carbs = Number(formData.carbs);

    if (changedMacro === 'protein') {
      protein = newValue;
      const proteinCals = protein * 4;
      const remainingCals = currentCalories - proteinCals;
      // Distribute remaining between fats (35%) and carbs (65%)
      fats = Math.round((remainingCals * 0.35) / 9);
      carbs = Math.round((remainingCals * 0.65) / 4);
    } else if (changedMacro === 'fats') {
      fats = newValue;
      const fatsCals = fats * 9;
      const remainingCals = currentCalories - fatsCals;
      // Distribute remaining between protein (40%) and carbs (60%)
      protein = Math.round((remainingCals * 0.40) / 4);
      carbs = Math.round((remainingCals * 0.60) / 4);
    } else {
      carbs = newValue;
      const carbsCals = carbs * 4;
      const remainingCals = currentCalories - carbsCals;
      // Distribute remaining between protein (55%) and fats (45%)
      protein = Math.round((remainingCals * 0.55) / 4);
      fats = Math.round((remainingCals * 0.45) / 9);
    }

    return { protein: Math.max(0, protein), fats: Math.max(0, fats), carbs: Math.max(0, carbs) };
  };

  const handleCaloriesChange = (value: string) => {
    const newCalories = Number(value) || 0;
    const { protein, fats, carbs } = recalculateFromCalories(newCalories);
    setFormData(prev => ({ ...prev, calories: value, protein, fats, carbs }));
  };

  const handleMacroChange = (macro: 'protein' | 'fats' | 'carbs', value: string) => {
    const newValue = Number(value) || 0;
    const recalculated = recalculateFromMacro(macro, newValue);
    setFormData(prev => ({ ...prev, ...recalculated, [macro]: value }));
  };

  const activityLevels = [
    { id: 'sedentary', label: 'Сидячий', desc: 'Офисная работа' },
    { id: 'light', label: 'Лёгкая', desc: '1-2 тренировки/нед' },
    { id: 'moderate', label: 'Умеренная', desc: '3-4 тренировки/нед' },
    { id: 'active', label: 'Высокая', desc: '5-6 тренировок/нед' },
    { id: 'very_active', label: 'Очень высокая', desc: 'Ежедневные тренировки' },
  ];

  const goals = [
    { id: 'lose', label: 'Похудеть', icon: TrendingDown },
    { id: 'maintain', label: 'Поддержать', icon: Target },
    { id: 'gain', label: 'Набрать', icon: TrendingUp },
  ];

  const paces = [
    { id: 'soft', label: 'Мягкий', desc: '~0.25 кг/нед' },
    { id: 'balanced', label: 'Средний', desc: '~0.5 кг/нед' },
    { id: 'aggressive', label: 'Быстрый', desc: '~0.75 кг/нед' },
  ];

  const handleSave = async () => {
    const payload = buildPayload();
    if (initialPayloadRef.current === JSON.stringify(payload)) {
      onBack();
      return;
    }

    setSaving(true);
    try {
      await onSave(payload);
      onBack();
    } catch (e) {
      console.error('Failed to save profile:', e);
      alert('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <button onClick={onBack} className="w-11 h-11 rounded-xl bg-section-bg flex items-center justify-center hover:bg-hover transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold">Анкета</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-50 shadow-lg"
          >
            {saving ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-32">
        {/* Физические параметры */}
        <div className="premium-card p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
            Физические параметры
          </h3>

          {/* Пол */}
          <div className="mb-4">
            <label className="text-sm text-muted-foreground mb-2 block">Пол</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ id: 'male', label: '👨 Мужской' }, { id: 'female', label: '👩 Женский' }].map(g => (
                <button
                  key={g.id}
                  onClick={() => setFormData(prev => ({ ...prev, gender: g.id }))}
                  className="p-4 rounded-2xl font-medium transition-all"
                  style={{
                    backgroundColor: formData.gender === g.id ? 'var(--primary)' : 'var(--card)',
                    color: formData.gender === g.id ? 'white' : 'var(--card-foreground)',
                    boxShadow: formData.gender === g.id ? '0 0 0 3px var(--primary)' : 'none'
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* Дата рождения / Возраст */}
          <div className="mb-4">
            <label className="text-sm text-muted-foreground mb-2 block">
              {editingBirthDate ? 'Дата рождения' : 'Возраст (нажми для изменения)'}
            </label>
            {editingBirthDate ? (
              <input
                type="date"
                value={formData.birthDate}
                onChange={e => setFormData(prev => ({ ...prev, birthDate: e.target.value }))}
                onBlur={() => setEditingBirthDate(false)}
                autoFocus
                className="w-full p-3 rounded-xl text-center font-semibold focus:outline-none text-base"
                style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
              />
            ) : (
              <button
                onClick={() => setEditingBirthDate(true)}
                className="w-full p-3 rounded-xl text-center font-semibold text-lg"
                style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
              >
                {calculateAge(formData.birthDate)} лет
              </button>
            )}
          </div>

          {/* Рост, Вес */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Рост, см</label>
              <input
                type="number"
                value={formData.height}
                onChange={e => setFormData(prev => ({ ...prev, height: e.target.value }))}
                className="w-full p-3 rounded-xl text-center font-semibold text-lg focus:outline-none"
                style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Вес, кг</label>
              <input
                type="number"
                value={formData.weight}
                onChange={e => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                className="w-full p-3 rounded-xl text-center font-semibold text-lg focus:outline-none"
                style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
              />
            </div>
          </div>
        </div>

        {/* Активность */}
        <div className="premium-card p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
              <Activity className="w-4 h-4 text-primary" />
            </div>
            Уровень активности
          </h3>
          <div className="space-y-2">
            {activityLevels.map(level => (
              <button
                key={level.id}
                onClick={() => setFormData(prev => ({ ...prev, activityLevel: level.id }))}
                className="w-full p-4 rounded-2xl text-left transition-all"
                style={{
                  backgroundColor: formData.activityLevel === level.id ? 'var(--primary)' : 'var(--card)',
                  color: formData.activityLevel === level.id ? 'white' : 'var(--card-foreground)',
                  boxShadow: formData.activityLevel === level.id ? '0 0 0 3px var(--primary)' : 'none'
                }}
              >
                <div className="font-medium">{level.label}</div>
                <div className="text-xs mt-0.5" style={{ opacity: formData.activityLevel === level.id ? 0.8 : 0.6 }}>
                  {level.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Цель */}
        <div className="premium-card p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
              <Target className="w-4 h-4 text-primary" />
            </div>
            Цель
          </h3>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {goals.map(g => (
              <button
                key={g.id}
                onClick={() => setFormData(prev => ({ ...prev, goal: g.id }))}
                className="p-4 rounded-2xl flex flex-col items-center gap-2 transition-all"
                style={{
                  backgroundColor: formData.goal === g.id ? 'var(--primary)' : 'var(--card)',
                  color: formData.goal === g.id ? 'white' : 'var(--card-foreground)',
                  boxShadow: formData.goal === g.id ? '0 0 0 3px var(--primary)' : 'none'
                }}
              >
                <g.icon className="w-6 h-6" />
                <span className="text-sm font-medium">{g.label}</span>
              </button>
            ))}
          </div>

          {/* Целевой вес */}
          {formData.goal !== 'maintain' && (
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-2 block">Целевой вес, кг</label>
              <input
                type="number"
                value={formData.targetWeight}
                onChange={e => setFormData(prev => ({ ...prev, targetWeight: e.target.value }))}
                className="w-full p-3 rounded-xl text-center font-semibold text-lg focus:outline-none"
                style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
              />
            </div>
          )}

          {/* Темп */}
          {formData.goal !== 'maintain' && (
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Темп изменения</label>
              <div className="grid grid-cols-3 gap-2">
                {paces.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setFormData(prev => ({ ...prev, pace: p.id }))}
                    className="p-3 rounded-xl text-center transition-all"
                    style={{
                      backgroundColor: formData.pace === p.id ? 'var(--primary)' : 'var(--card)',
                      color: formData.pace === p.id ? 'white' : 'var(--card-foreground)',
                      boxShadow: formData.pace === p.id ? '0 0 0 3px var(--primary)' : 'none'
                    }}
                  >
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-[10px] mt-0.5" style={{ opacity: formData.pace === p.id ? 0.8 : 0.6 }}>
                      {p.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* КБЖУ */}
        <div className="premium-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                <Flame className="w-4 h-4 text-primary" />
              </div>
              Калории и БЖУ
            </h3>
            <button
              onClick={() => setFormData(prev => ({ ...prev, customKBJU: !prev.customKBJU }))}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: formData.customKBJU ? 'var(--primary)' : 'var(--card)',
                color: formData.customKBJU ? 'white' : 'var(--card-foreground)'
              }}
            >
              {formData.customKBJU ? '✏️ Вручную' : '🤖 Авто'}
            </button>
          </div>

          {formData.customKBJU ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Калории в день</label>
                <input
                  type="number"
                  value={formData.calories}
                  onChange={e => handleCaloriesChange(e.target.value)}
                  className="w-full p-4 rounded-xl font-semibold text-xl text-center focus:outline-none"
                  style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--protein-bg)' }}>
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--protein-text)' }}>Белки, г</label>
                  <input
                    type="number"
                    value={formData.protein}
                    onChange={e => handleMacroChange('protein', e.target.value)}
                    className="w-full p-2 rounded-lg text-center font-semibold focus:outline-none"
                    style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
                  />
                </div>
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--fats-bg)' }}>
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--fats-text)' }}>Жиры, г</label>
                  <input
                    type="number"
                    value={formData.fats}
                    onChange={e => handleMacroChange('fats', e.target.value)}
                    className="w-full p-2 rounded-lg text-center font-semibold focus:outline-none"
                    style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
                  />
                </div>
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--carbs-bg)' }}>
                  <label className="text-xs font-medium mb-2 block" style={{ color: 'var(--carbs-text)' }}>Углеводы, г</label>
                  <input
                    type="number"
                    value={formData.carbs}
                    onChange={e => handleMacroChange('carbs', e.target.value)}
                    className="w-full p-2 rounded-lg text-center font-semibold focus:outline-none"
                    style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 rounded-2xl" style={{ backgroundColor: 'var(--card)', color: 'var(--card-foreground)' }}>
              <div className="text-3xl mb-2">🤖</div>
              <p className="text-sm font-medium">КБЖУ рассчитываются автоматически</p>
              <p className="text-xs mt-1" style={{ opacity: 0.6 }}>на основе ваших параметров и цели</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== SETTINGS MENU + NOTIFICATIONS =====
interface SettingsMenuScreenProps {
  onBack: () => void;
  onOpenQuestionnaire: () => void;
  onOpenNotifications: () => void;
}

function SettingsMenuScreen({ onBack, onOpenQuestionnaire, onOpenNotifications }: SettingsMenuScreenProps) {
  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <button onClick={onBack} className="w-11 h-11 rounded-xl bg-section-bg flex items-center justify-center hover:bg-hover transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold">Настройки</h1>
          <div className="w-11 h-11" />
        </div>
      </div>

      <div className="p-4 space-y-3 pb-32">
        <button
          onClick={onOpenQuestionnaire}
          className="premium-card p-5 w-full flex items-center justify-between hover:bg-hover transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Анкета</div>
              <div className="text-xs text-muted-foreground">Пол, возраст, вес, цель, КБЖУ</div>
            </div>
          </div>
          <ChevronLeft className="w-5 h-5 rotate-180 opacity-60" />
        </button>

        <button
          onClick={onOpenNotifications}
          className="premium-card p-5 w-full flex items-center justify-between hover:bg-hover transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Уведомления</div>
              <div className="text-xs text-muted-foreground">Напоминания о приёмах, КБЖУ и весе</div>
            </div>
          </div>
          <ChevronLeft className="w-5 h-5 rotate-180 opacity-60" />
        </button>
      </div>
    </div>
  );
}

interface NotificationsScreenProps {
  onBack: () => void;
  profile: any;
  onSave: (data: any) => Promise<void>;
}

function NotificationsScreen({ onBack, profile, onSave }: NotificationsScreenProps) {
  const [saving, setSaving] = useState(false);

  type MealReminderForm = {
    id: string;
    mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    enabled: boolean;
    time: string;
  };

  const resolvedTz =
    profile?.notificationSettings?.timezone ||
    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null) ||
    'Europe/Moscow';

  const initialMealReminders = Array.isArray(profile?.notificationSettings?.mealReminders) && profile.notificationSettings.mealReminders.length
    ? profile.notificationSettings.mealReminders
    : [
      { id: 'default-breakfast', hour: 9, minute: 0, mealType: 'breakfast', enabled: true },
      { id: 'default-lunch', hour: 13, minute: 0, mealType: 'lunch', enabled: true },
      { id: 'default-dinner', hour: 19, minute: 0, mealType: 'dinner', enabled: true },
    ];

  const initialWeightTime = profile?.notificationSettings?.weightTracking
    ? { hour: profile.notificationSettings.weightTracking.hour, minute: profile.notificationSettings.weightTracking.minute }
    : { hour: 10, minute: 0 };

  const [timezone] = useState(resolvedTz);
  const [mealReminders, setMealReminders] = useState<MealReminderForm[]>(
    initialMealReminders.map((r: any) => ({
      id: String(r.id),
      mealType: r.mealType as MealReminderForm['mealType'],
      enabled: r.enabled !== false,
      time: `${String(r.hour).padStart(2, '0')}:${String(r.minute).padStart(2, '0')}`
    }))
  );
  const [macroUnderfillEnabled, setMacroUnderfillEnabled] = useState(Boolean(profile?.notificationSettings?.macroUnderfill?.enabled));
  const [weightTrackingEnabled, setWeightTrackingEnabled] = useState(Boolean(profile?.notificationSettings?.weightTracking?.enabled));
  const [weightTrackingTime, setWeightTrackingTime] = useState(
    `${String(initialWeightTime.hour).padStart(2, '0')}:${String(initialWeightTime.minute).padStart(2, '0')}`
  );

  const buildPayload = () => {
    const parseTime = (t: string, fallback: { hour: number; minute: number }) => {
      const m = String(t || '').match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return fallback;
      const hour = Number(m[1]);
      const minute = Number(m[2]);
      if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) return fallback;
      return { hour, minute };
    };

    return {
      timezone,
      mealReminders: mealReminders.map((r) => {
        const tm = parseTime(r.time, { hour: 9, minute: 0 });
        return {
          id: String(r.id),
          hour: tm.hour,
          minute: tm.minute,
          mealType: r.mealType,
          enabled: r.enabled !== false,
        };
      }),
      macroUnderfillEnabled,
      weightTrackingEnabled,
      weightTrackingTime: parseTime(weightTrackingTime, { hour: 10, minute: 0 }),
    };
  };

  const initialPayloadRef = useRef(
    JSON.stringify({
      timezone: resolvedTz,
      mealReminders: initialMealReminders.map((r: any) => ({ id: r.id, hour: r.hour, minute: r.minute, mealType: r.mealType, enabled: r.enabled !== false })),
      macroUnderfillEnabled: Boolean(profile?.notificationSettings?.macroUnderfill?.enabled),
      weightTrackingEnabled: Boolean(profile?.notificationSettings?.weightTracking?.enabled),
      weightTrackingTime: initialWeightTime,
    })
  );

  const handleAddReminder = () => {
    const id = (globalThis as any).crypto?.randomUUID?.() || `r_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    setMealReminders((prev) => ([
      ...prev,
      { id, mealType: 'lunch', enabled: true, time: '13:00' }
    ]));
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (initialPayloadRef.current === JSON.stringify(payload)) {
      onBack();
      return;
    }

    setSaving(true);
    try {
      await onSave(payload);
      onBack();
    } catch (e) {
      console.error('Failed to save notifications:', e);
      alert('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <button onClick={onBack} className="w-11 h-11 rounded-xl bg-section-bg flex items-center justify-center hover:bg-hover transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold">Уведомления</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-50 shadow-lg"
          >
            {saving ? '...' : 'Сохранить'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-32">
        <div className="premium-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              Напоминания о приёмах пищи
            </h3>
            <button
              onClick={handleAddReminder}
              className="w-10 h-10 rounded-xl bg-section-bg/50 flex items-center justify-center hover:bg-section-bg transition-colors"
              title="Добавить"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-3">
            {mealReminders.map((r) => (
              <div key={r.id} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--card)' }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="time"
                      value={r.time}
                      onChange={(e) => setMealReminders((prev) => prev.map((x) => x.id === r.id ? { ...x, time: e.target.value } : x))}
                      className="p-2 rounded-lg font-semibold focus:outline-none"
                      style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
                    />
                    <select
                      value={r.mealType}
                      onChange={(e) => setMealReminders((prev) => prev.map((x) => x.id === r.id ? { ...x, mealType: e.target.value as MealReminderForm['mealType'] } : x))}
                      className="p-2 rounded-lg focus:outline-none"
                      style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
                    >
                      <option value="breakfast">Завтрак</option>
                      <option value="lunch">Обед</option>
                      <option value="dinner">Ужин</option>
                      <option value="snack">Перекус</option>
                    </select>
                  </div>

                  <button
                    onClick={() => setMealReminders((prev) => prev.map((x) => x.id === r.id ? { ...x, enabled: !x.enabled } : x))}
                    className="px-3 py-2 rounded-xl text-sm font-medium transition-all"
                    style={{
                      backgroundColor: r.enabled ? 'var(--primary)' : 'var(--section-bg)',
                      color: r.enabled ? 'white' : 'var(--foreground)'
                    }}
                  >
                    {r.enabled ? 'Вкл' : 'Выкл'}
                  </button>

                  <button
                    onClick={() => setMealReminders((prev) => prev.filter((x) => x.id !== r.id))}
                    className="w-10 h-10 rounded-xl bg-section-bg/50 flex items-center justify-center hover:bg-section-bg transition-colors"
                    title="Удалить"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="premium-card p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Недобор КБЖУ</h3>
            <button
              onClick={() => setMacroUnderfillEnabled(v => !v)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: macroUnderfillEnabled ? 'var(--primary)' : 'var(--section-bg)',
                color: macroUnderfillEnabled ? 'white' : 'var(--foreground)'
              }}
            >
              {macroUnderfillEnabled ? 'Включено' : 'Выключено'}
            </button>
          </div>
          <div className="text-sm text-muted-foreground">
            В 18:00 напомним, если по любому из показателей &lt; 90% от цели.
          </div>
        </div>

        <div className="premium-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Отслеживание веса</h3>
            <button
              onClick={() => setWeightTrackingEnabled(v => !v)}
              className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: weightTrackingEnabled ? 'var(--primary)' : 'var(--section-bg)',
                color: weightTrackingEnabled ? 'white' : 'var(--foreground)'
              }}
            >
              {weightTrackingEnabled ? 'Включено' : 'Выключено'}
            </button>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">Раз в 14 дней</div>
            <input
              type="time"
              value={weightTrackingTime}
              onChange={e => setWeightTrackingTime(e.target.value)}
              className="p-2 rounded-lg font-semibold focus:outline-none"
              style={{ backgroundColor: 'var(--section-bg)', color: 'var(--foreground)' }}
            />
          </div>
        </div>

        <div className="premium-card p-5">
          <div className="text-xs text-muted-foreground">
            Часовой пояс: {timezone}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== PROFILE PAGE COMPONENT =====
interface ProfilePageProps {
  onBack: () => void;
  weightData: WeightData[];
  achievements: any[];
  challenges: any[];
  onAddWeight: (weight: number) => void;
  onJoinChallenge: (id: string) => void;
  onUpdateSettings: (settings: any) => void;
  onSendMessage: (message: string) => void;
  initialShowSettings?: boolean;
}

function ProfilePage({
  onBack,
  weightData,
  onAddWeight,
  profile,
  onProfileUpdate,
  initialShowSettings = false,
}: ProfilePageProps & { profile?: any; onProfileUpdate?: () => void; initialShowSettings?: boolean }) {
  const [activeTab, setActiveTab] = useState<'weight' | 'achievements' | 'referral'>('weight');
  const [showSettings, setShowSettings] = useState(initialShowSettings);
  const [settingsSection, setSettingsSection] = useState<'menu' | 'questionnaire' | 'notifications'>(
    initialShowSettings ? 'questionnaire' : 'menu'
  );
  const { theme, toggleTheme } = useTheme();
  const [referralData, setReferralData] = useState<any>(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const api = useApi();

  // Open settings when initialShowSettings changes to true
  useEffect(() => {
    if (initialShowSettings) {
      setShowSettings(true);
      setSettingsSection('questionnaire');
    }
  }, [initialShowSettings]);

  // Fetch referral data when tab is opened
  useEffect(() => {
    if (activeTab === 'referral' && !referralData) {
      setReferralLoading(true);
      api.request('/miniapp/referral')
        .then(res => {
          if (res.success) setReferralData(res.data);
        })
        .catch(console.error)
        .finally(() => setReferralLoading(false));
    }
  }, [activeTab, referralData, api]);

  const handleCopyCode = () => {
    if (referralData?.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = () => {
    if (referralData?.inviteLink) {
      const text = `🍎 Попробуй Noma — умный подсчёт калорий!\n\nИспользуй мой промокод: ${referralData.referralCode}\n\n${referralData.inviteLink}`;
      if (navigator.share) {
        navigator.share({ title: 'Noma', text }).catch(() => { });
      } else {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  // Берём реальные данные профиля с fallback
  const profileData = {
    name: profile?.name || 'User',
    username: profile?.username || 'user',
    avatar: profile?.avatar || null,
    currentWeight: Number(profile?.currentWeight) || 75,
    goalWeight: Number(profile?.goalWeight) || 70,
    height: Number(profile?.height) || 175,
    age: Number(profile?.age) || 25,
    activityLevel: profile?.activityLevel || 'medium',
    streak: profile?.stats?.streak || 0,
    streakActiveToday: profile?.stats?.streakActiveToday ?? true,
    totalDays: profile?.stats?.totalDays || 0,
  };

  const tabs = [
    { id: 'weight', label: 'Вес', icon: Weight, disabled: false },
    { id: 'referral', label: 'Друзья', icon: UserPlus, disabled: false },
    { id: 'achievements', label: 'Награды', icon: Trophy, disabled: true },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'weight':
        return (
          <WeightTracker
            weightData={weightData}
            onAddWeight={onAddWeight}
          />
        );
      case 'referral':
        return (
          <div className="space-y-4">
            {referralLoading ? (
              <div className="text-center py-8 text-muted-foreground">Загрузка...</div>
            ) : referralData ? (
              <>
                {/* Invite Card */}
                <div className="premium-card p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7B7BA8] to-[#595880] flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Пригласи друзей</h3>
                      <p className="text-sm text-muted-foreground">Получайте по 7 дней премиума</p>
                    </div>
                  </div>

                  {/* Promo Code */}
                  <div className="bg-section-bg/50 rounded-xl p-4 mb-4">
                    <p className="text-xs text-muted-foreground mb-2">Твой промокод</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold tracking-widest text-[#7B7BA8]">
                        {referralData.referralCode}
                      </span>
                      <button
                        onClick={handleCopyCode}
                        className="p-2 rounded-lg bg-[#595880]/20 hover:bg-[#595880]/30 transition-colors"
                      >
                        {copied ? <Check className="w-5 h-5 text-success" /> : <Copy className="w-5 h-5 text-[#7B7BA8]" />}
                      </button>
                    </div>
                  </div>

                  {/* Share Button */}
                  <button
                    onClick={handleShare}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-[#7B7BA8] to-[#595880] text-white font-semibold flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-5 h-5" />
                    Поделиться ссылкой
                  </button>
                </div>

                {/* Stats */}
                <div className="premium-card p-5">
                  <h4 className="font-semibold mb-4">Твоя статистика</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-section-bg/50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-[#7B7BA8]">{(referralData.activatedCount || 0) + (referralData.pendingCount || 0)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Приглашено</p>
                    </div>
                    <div className="bg-section-bg/50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-[#7B7BA8]">{referralData.activatedCount || 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">Активировано</p>
                    </div>
                    <div className="bg-section-bg/50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-success">{referralData.totalRewardDays}</p>
                      <p className="text-xs text-muted-foreground mt-1">Дней</p>
                    </div>
                  </div>
                </div>

                {/* Level Progress */}
                <div className="premium-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-[#7B7BA8]" />
                      <span className="font-semibold">{referralData.levelName}</span>
                    </div>
                    {referralData.isAmbassador && (
                      <span className="px-2 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-semibold rounded-full">
                        👑 Ambassador
                      </span>
                    )}
                  </div>

                  {referralData.nextLevelName && (
                    <>
                      <div className="flex justify-between text-sm text-muted-foreground mb-2">
                        <span>До уровня «{referralData.nextLevelName}»</span>
                        <span>{referralData.activatedCount || 0}/{referralData.nextLevelRequired}</span>
                      </div>
                      <div className="h-2 bg-section-bg/50 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#7B7BA8] to-[#595880] rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, ((referralData.activatedCount || 0) / referralData.nextLevelRequired) * 100)}%` }}
                        />
                      </div>
                    </>
                  )}

                  {/* Level rewards info */}
                  <div className="mt-4 pt-4 border-t border-border/30">
                    <p className="text-xs text-muted-foreground mb-2">Награды за уровни:</p>
                    <div className="space-y-2 text-sm">
                      <div className={`flex justify-between ${referralData.level >= 1 ? 'text-success' : 'text-muted-foreground'}`}>
                        <span>🏅 5 друзей</span>
                        <span>+30 дней премиума</span>
                      </div>
                      <div className={`flex justify-between ${referralData.level >= 2 ? 'text-success' : 'text-muted-foreground'}`}>
                        <span>🏆 10 друзей</span>
                        <span>+90 дней премиума</span>
                      </div>
                      <div className={`flex justify-between ${referralData.level >= 3 ? 'text-success' : 'text-muted-foreground'}`}>
                        <span>👑 50 друзей</span>
                        <span>Вечный премиум</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Не удалось загрузить данные
              </div>
            )}
          </div>
        );
      case 'achievements':
        return (
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>Достижения временно недоступны</p>
          </div>
        );
      default:
        return null;
    }
  };

  const handleSaveSettings = async (data: any) => {
    // Always clear weight goal flags when saving settings
    Object.keys(localStorage)
      .filter(k => k.startsWith('weight_goal_shown_'))
      .forEach(k => localStorage.removeItem(k));

    await api.request('/miniapp/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Trigger refresh of user data and wait for completion
    await onProfileUpdate?.();
  };

  const handleSaveNotifications = async (data: any) => {
    await api.request('/miniapp/notifications', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await onProfileUpdate?.();
  };

  // Show settings screen if active
  if (showSettings) {
    if (settingsSection === 'menu') {
      return (
        <SettingsMenuScreen
          onBack={() => {
            setShowSettings(false);
            setSettingsSection('menu');
          }}
          onOpenQuestionnaire={() => setSettingsSection('questionnaire')}
          onOpenNotifications={() => setSettingsSection('notifications')}
        />
      );
    }

    if (settingsSection === 'questionnaire') {
      return (
        <SettingsScreen
          onBack={() => setSettingsSection('menu')}
          profile={profile}
          onSave={handleSaveSettings}
        />
      );
    }

    return (
      <NotificationsScreen
        onBack={() => setSettingsSection('menu')}
        profile={profile}
        onSave={handleSaveNotifications}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Хедер */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onBack}
            className="w-11 h-11 rounded-xl bg-section-bg/50 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold">Профиль</h1>
          <button
            onClick={toggleTheme}
            className="w-11 h-11 rounded-xl bg-section-bg/50 flex items-center justify-center"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-6 pb-32">
        {/* Профильная карточка */}
        <div className="premium-card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-shrink-0">
              {profileData.avatar ? (
                <img src={profileData.avatar} alt="Profile" className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #595880 0%, #7B7BA8 100%)' }}>
                  <User className="w-8 h-8 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{profileData.name}</h2>
                  <p className="text-muted-foreground text-sm">@{profileData.username}</p>
                </div>
                <button
                  onClick={() => {
                    setSettingsSection('menu');
                    setShowSettings(true);
                  }}
                  className="w-10 h-10 rounded-xl bg-section-bg/50 flex items-center justify-center hover:bg-section-bg transition-colors"
                >
                  <Settings className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{profileData.totalDays} дней</span>
                </div>
                <div className="flex items-center gap-1">
                  {(() => {
                    const streak = Number(profileData.streak) || 0;
                    const activeToday = Boolean(profileData.streakActiveToday);
                    const milestones = new Set([3, 7, 14, 30, 60, 100]);
                    if (streak <= 0) {
                      return (
                        <>
                          <span className="w-4 h-4 rounded-full bg-muted/30 flex items-center justify-center">
                            <ZapOff className="w-3 h-3 text-muted-foreground" />
                          </span>
                          <span className="text-xs text-muted-foreground">нет серии</span>
                        </>
                      );
                    }
                    if (!activeToday) {
                      return (
                        <>
                          <span className="w-4 h-4 rounded-full bg-muted/30 flex items-center justify-center">
                            <ZapOff className="w-3 h-3 text-muted-foreground" />
                          </span>
                          <span className="text-xs text-muted-foreground">{streak} подряд</span>
                        </>
                      );
                    }
                    if (milestones.has(streak)) {
                      return (
                        <>
                          <span className="w-4 h-4 rounded-full bg-primary/15 flex items-center justify-center">
                            <Trophy className="w-3 h-3 text-primary" />
                          </span>
                          <span className="text-xs text-primary font-medium">{streak} подряд</span>
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="w-4 h-4 rounded-full bg-warning/15 flex items-center justify-center">
                          <Zap className="w-3 h-3 text-warning" />
                        </span>
                        <span className="text-xs text-warning font-medium">{streak} подряд</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Вкладки */}
        <div className="premium-card p-4">
          <div className="profile-tabs-centered mb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id as any)}
                disabled={tab.disabled}
                className={`profile-tab-centered ${activeTab === tab.id ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
          <div>{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
}

// ===== MAIN APP COMPONENT =====
function SubscriptionScreen({
  onBack,
  onOpenOffer,
  onOpenPrivacy,
}: {
  onBack: () => void;
  onOpenOffer: () => void;
  onOpenPrivacy: () => void;
}) {
  const api = useApi();

  const [type, setType] = useState<'monthly' | 'yearly'>('monthly');
  const [method, setMethod] = useState<'bank_card' | 'sbp' | 'sberbank'>('bank_card');
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quotes, setQuotes] = useState<{ monthly?: any; yearly?: any }>({});
  const [cancelPromoBusy, setCancelPromoBusy] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const plan = params.get('plan');
      const m = params.get('method');
      if (plan === 'yearly' || plan === 'monthly') setType(plan);
      if (m === 'sbp' || m === 'bank_card' || m === 'sberbank') setMethod(m);
    } catch {
      // ignore
    }
  }, []);

  const baseMonthly = 299;
  const baseYearly = 2499;

  const selectedQuote = type === 'monthly' ? quotes.monthly : quotes.yearly;
  const basePrice = type === 'monthly' ? baseMonthly : baseYearly;
  const finalPrice = typeof selectedQuote?.data?.finalAmount === 'number' ? selectedQuote.data.finalAmount : basePrice;
  const appliedPromo = selectedQuote?.data?.promo || null;
  const blockedByTelegram = !!selectedQuote?.data?.blockedByTelegram;
  const isFree = !!selectedQuote?.data?.free;

  const monthlyFinal = typeof quotes.monthly?.data?.finalAmount === 'number' ? quotes.monthly.data.finalAmount : baseMonthly;
  const yearlyFinal = typeof quotes.yearly?.data?.finalAmount === 'number' ? quotes.yearly.data.finalAmount : baseYearly;
  const monthlyHasDiscount = !!quotes.monthly?.data?.promo && monthlyFinal !== baseMonthly;
  const yearlyHasDiscount = !!quotes.yearly?.data?.promo && yearlyFinal !== baseYearly;

  const refreshQuotes = useCallback(async () => {
    try {
      setQuoteLoading(true);
      const [m, y] = await Promise.all([
        api.request('/miniapp/payment/quote', { method: 'POST', body: JSON.stringify({ type: 'monthly' }) }),
        api.request('/miniapp/payment/quote', { method: 'POST', body: JSON.stringify({ type: 'yearly' }) }),
      ]);
      setQuotes({ monthly: m, yearly: y });
    } catch {
      // ignore
    } finally {
      setQuoteLoading(false);
    }
  }, [api]);

  const cancelPromo = useCallback(async () => {
    try {
      setCancelPromoBusy(true);
      const resp = await api.request('/miniapp/promo/cancel', { method: 'POST' });
      if (resp?.success === false) {
        alert(resp?.error || 'Не удалось отменить промокод');
        return;
      }
      await refreshQuotes();
    } catch (e: any) {
      alert(e?.message || 'Не удалось отменить промокод');
    } finally {
      setCancelPromoBusy(false);
    }
  }, [api, refreshQuotes]);

  useEffect(() => {
    refreshQuotes();
  }, [refreshQuotes]);

  const handlePay = async () => {
    try {
      if (blockedByTelegram) {
        alert('Промокод уже применён для оплаты через бота. Завершите оплату в боте или отмените промокод.');
        return;
      }

      setSubmitting(true);
      const resp = await api.request('/miniapp/payment/create', {
        method: 'POST',
        body: JSON.stringify({
          type,
          method,
          savePaymentMethod,
        }),
      });

      if (resp?.success && resp?.data?.freeActivated) {
        alert('✅ Подписка активирована по промокоду');
        onBack();
        return;
      }

      const url = resp?.data?.confirmationUrl;
      if (!resp?.success || !url) {
        alert(resp?.error || 'Не удалось создать платёж');
        return;
      }

      openExternalLink(url);
    } catch (e: any) {
      alert(e?.message || 'Не удалось создать платёж');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onBack}
          className="p-2 rounded-xl bg-section-bg/60 hover:bg-section-bg transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="text-lg font-semibold">Подписка</div>
          <div className="text-sm text-muted-foreground">Оплата через YooKassa</div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="premium-card p-5">
          <div className="text-sm font-semibold mb-3">Тариф</div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setType('monthly')}
              className={`p-4 text-left border rounded-2xl transition-colors ${type === 'monthly' ? 'border-primary bg-primary/10' : 'border-border/30 bg-section-bg/40'}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold">Месяц</div>
                {type === 'monthly' && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="text-sm text-foreground/90 mt-1">
                {quoteLoading ? (
                  `${baseMonthly}₽`
                ) : monthlyHasDiscount ? (
                  <>
                    <span className="line-through text-muted-foreground mr-2">{baseMonthly}₽</span>
                    <span className="font-semibold">{monthlyFinal}₽</span>
                  </>
                ) : (
                  `${baseMonthly}₽`
                )}
              </div>
            </button>
            <button
              onClick={() => setType('yearly')}
              className={`p-4 text-left border rounded-2xl transition-colors ${type === 'yearly' ? 'border-primary bg-primary/10' : 'border-border/30 bg-section-bg/40'}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold">Год</div>
                {type === 'yearly' && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="text-sm text-foreground/90 mt-1">
                {quoteLoading ? (
                  `${baseYearly}₽`
                ) : yearlyHasDiscount ? (
                  <>
                    <span className="line-through text-muted-foreground mr-2">{baseYearly}₽</span>
                    <span className="font-semibold">{yearlyFinal}₽</span>
                  </>
                ) : (
                  `${baseYearly}₽`
                )}
              </div>
            </button>
          </div>

          {appliedPromo ? (
            <div className="mt-3 flex items-center justify-between gap-3 text-xs text-muted-foreground">
              <div>
                Промокод применён: <span className="font-medium">{appliedPromo.code}</span>{' '}
                ({appliedPromo.type === 'discount_percent' ? `-${appliedPromo.value}%` : `-${appliedPromo.value}₽`})
                {isFree ? ' — сумма к оплате 0₽' : null}
              </div>
              <button
                onClick={cancelPromo}
                disabled={cancelPromoBusy}
                className="p-2 rounded-xl bg-section-bg/60 hover:bg-section-bg transition-colors disabled:opacity-50"
                aria-label="Отменить промокод"
                title="Отменить промокод"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : null}

          {blockedByTelegram ? (
            <div className="mt-3 text-xs text-warning">
              Промокод сейчас привязан к оплате через бота. Завершите оплату в боте.
            </div>
          ) : null}
        </div>

        <div className="premium-card p-5">
          <div className="text-sm font-semibold mb-3">Способ оплаты</div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setMethod('bank_card')}
              className={`p-4 text-left border rounded-2xl transition-colors ${method === 'bank_card' ? 'border-primary bg-primary/10' : 'border-border/30 bg-section-bg/40'}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold">Карта</div>
                {method === 'bank_card' && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="text-sm text-foreground/90 mt-1">Visa / Mastercard / МИР</div>
            </button>
            <button
              onClick={() => setMethod('sbp')}
              className={`p-4 text-left border rounded-2xl transition-colors ${method === 'sbp' ? 'border-primary bg-primary/10' : 'border-border/30 bg-section-bg/40'}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold">СБП</div>
                {method === 'sbp' && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="text-sm text-foreground/90 mt-1">По QR в приложении банка</div>
            </button>
            <button
              onClick={() => setMethod('sberbank')}
              className={`p-4 text-left border rounded-2xl transition-colors ${method === 'sberbank' ? 'border-primary bg-primary/10' : 'border-border/30 bg-section-bg/40'}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold">СберПэй</div>
                {method === 'sberbank' && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="text-sm text-foreground/90 mt-1">Оплата в СберБанк Онлайн</div>
            </button>
          </div>

          <label className="mt-4 flex items-start gap-3">
            <input
              type="checkbox"
              checked={savePaymentMethod}
              onChange={(e) => setSavePaymentMethod(e.target.checked)}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-medium">Привязать способ оплаты для автопродления</div>
              <div className="text-xs text-muted-foreground">
                Можно отключить — тогда продление будет вручную
              </div>
            </div>
          </label>

          <div className="text-xs text-muted-foreground mt-3">
            {savePaymentMethod
              ? 'Мы привяжем способ оплаты для автопродления. Отвязать можно в боте в разделе «Подписка».'
              : 'Способ оплаты не будет привязан — продление будет вручную.'}
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={submitting || blockedByTelegram}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#7B7BA8] to-[#595880] text-white font-semibold"
        >
          {submitting ? 'Создаём платёж…' : (isFree ? 'Активировать бесплатно' : `Оплатить ${finalPrice}₽`)}
        </button>

        <div className="text-xs text-muted-foreground text-center">
          Оплачивая, вы соглашаетесь с{' '}
          <button
            type="button"
            onClick={onOpenOffer}
            className="underline underline-offset-2 hover:text-foreground"
          >
            публичной офертой
          </button>
          {' '}и{' '}
          <button
            type="button"
            onClick={onOpenPrivacy}
            className="underline underline-offset-2 hover:text-foreground"
          >
            политикой конфиденциальности
          </button>
          .
        </div>
      </div>
    </div>
  );
}

function LegalTextScreen({
  title,
  subtitle,
  text,
  onBack,
}: {
  title: string;
  subtitle?: string;
  text: string;
  onBack: () => void;
}) {
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onBack}
          className="p-2 rounded-xl bg-section-bg/60 hover:bg-section-bg transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="text-lg font-semibold">{title}</div>
          {subtitle ? <div className="text-sm text-muted-foreground">{subtitle}</div> : null}
        </div>
      </div>

      <div className="premium-card p-5">
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

const OFFER_TEXT = `ПУБЛИЧНАЯ ОФЕРТА

о предоставлении доступа к сервису Noma

Настоящий документ является официальным предложением сервиса Noma (далее — «Сервис») заключить договор на условиях, изложенных ниже, с любым дееспособным лицом (далее — «Пользователь»).

1. Общие положения

1.1. Акцептом оферты считается прохождение анкеты, использование Сервиса и/или оплата подписки.
1.2. С момента акцепта договор считается заключённым.
1.3. Оферта действует бессрочно до её изменения или отзыва.

2. Предмет договора

2.1. Пользователю предоставляется доступ к цифровому функционалу Сервиса Noma через Telegram-бота.
2.2. В рамках подписки Пользователь получает:
— анализ фото еды с использованием AI;
— неограниченный дневник питания;
— подробную статистику;
— персональные рекомендации на основе данных Пользователя.
2.3. Лимит анализов — 15 в сутки.
2.4. Сервис не является медицинской услугой и носит информационный характер.

3. Пробный период

3.1. После прохождения анкеты Пользователю предоставляется 1 пробный день.
3.2. Пробный период предоставляется без привязки банковской карты.
3.3. По окончании пробного периода Пользователь самостоятельно принимает решение о покупке подписки.

4. Подписка и оплата

4.1. Доступ предоставляется по подписке:
— Месячная — 299 ₽ / 30 дней
— Годовая — 2499 ₽ / 365 дней
4.2. Оплата осуществляется через платёжный сервис ЮKassa.
4.3. Пользователь может выбрать:
— разовую оплату;
— оплату с подключением автосписания.
4.4. Автосписание может быть отключено Пользователем в любой момент.

5. Возвраты

5.1. Возврат денежных средств не предусмотрен, так как услуга является цифровой и считается оказанной с момента предоставления доступа.

6. Права и обязанности

6.1. Сервис вправе:
— изменять тарифы и функционал;
— проводить технические работы;
— при сбоях компенсировать доступ бонусными днями или иным способом по своему усмотрению.
6.2. Пользователь обязуется использовать Сервис исключительно в личных целях и не нарушать его работу.

7. Ответственность

7.1. Сервис не гарантирует достижение конкретных результатов.
7.2. Пользователь самостоятельно несёт ответственность за решения, принятые на основе рекомендаций Сервиса.

8. Возрастные ограничения

8.1. Минимальный возраст Пользователя — 14 лет.
8.2. Пользователи от 14 до 18 лет используют Сервис с согласия законного представителя.

9. Персональные данные

9.1. Пользователь даёт согласие на обработку персональных данных, включая данные анкеты, фото еды и статистику.
9.2. Обработка данных осуществляется в соответствии с Политикой конфиденциальности Сервиса.

10. Реквизиты Исполнителя

Исполнитель: самозанятый
Гриб Алексей Александрович
ИНН 502993247093
Контакт: t.me/lft_manager
`;

const PRIVACY_TEXT = `ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ

сервиса Noma

Настоящая Политика определяет порядок обработки и защиты персональных данных пользователей сервиса Noma (далее — «Сервис»).

1. Общие положения

1.1. Используя Сервис, Пользователь подтверждает согласие с настоящей Политикой.
1.2. Оператором персональных данных является самозанятый, указанный в разделе 10.
1.3. Обработка данных осуществляется в соответствии с законодательством РФ.

2. Какие данные собираются

Сервис может обрабатывать следующие данные:
• данные анкеты (возраст, цели, параметры тела);
• фотографии еды, загружаемые Пользователем;
• данные о рационе и статистике использования сервиса;
• технические данные (ID пользователя Telegram).

Сервис не запрашивает и не хранит платёжные данные — оплата обрабатывается ЮKassa.

3. Цели обработки данных

Персональные данные обрабатываются для:
• предоставления доступа к функционалу Сервиса;
• анализа рациона и формирования рекомендаций;
• ведения статистики и улучшения качества сервиса;
• связи с Пользователем при необходимости.

4. Правовые основания

4.1. Обработка данных осуществляется на основании согласия Пользователя.
4.2. Пользователь предоставляет данные добровольно.

5. Условия обработки и хранения

5.1. Данные обрабатываются автоматизированным способом.
5.2. Сервис принимает разумные меры для защиты данных от утечки и несанкционированного доступа.
5.3. Данные хранятся в течение срока использования Сервиса либо до отзыва согласия.

6. Передача данных третьим лицам

6.1. Персональные данные не передаются третьим лицам, за исключением случаев, предусмотренных законом.
6.2. Платёжные операции осуществляются через ЮKassa, которая самостоятельно обрабатывает платёжные данные.

7. Данные, связанные с питанием и здоровьем

7.1. Рекомендации Сервиса носят информационный характер.
7.2. Сервис не является медицинской услугой и не заменяет консультацию специалиста.
7.3. Пользователь самостоятельно принимает решения на основе полученной информации.

8. Права пользователя

Пользователь имеет право:
• запросить информацию о своих данных;
• отозвать согласие на обработку данных;
• прекратить использование Сервиса.

Для этого необходимо связаться с оператором по контактам ниже.

9. Возрастные ограничения

9.1. Сервис доступен пользователям от 14 лет.
9.2. Пользователи от 14 до 18 лет используют Сервис с согласия законного представителя.

10. Контакты оператора

Оператор персональных данных:
самозанятый Гриб Алексей Александрович
ИНН 502993247093
Контакт: t.me/lft_manager
`;

function AppContent() {
  const [activeTab, setActiveTab] = useState('home');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [hasTips, setHasTips] = useState(false);
  const [tipsEnabled, setTipsEnabled] = useState(false);
  const [tipsModalOpen, setTipsModalOpen] = useState(false);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [tipsTitle, setTipsTitle] = useState('Подсказки на сегодня');
  const [tipsList, setTipsList] = useState<string[]>([]);
  const [tipsAdviceKey, setTipsAdviceKey] = useState<string | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [legalView, setLegalView] = useState<null | 'offer' | 'privacy'>(null);
  const [openSettingsOnProfile, setOpenSettingsOnProfile] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [_loading, _setLoading] = useState(true);
  const [_error, _setError] = useState<string | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [onboardingRequired, setOnboardingRequired] = useState<boolean>(() => {
    try {
      return localStorage.getItem('miniapp_onboarding_required') === 'true';
    } catch {
      return false;
    }
  });
  const onboardingRequiredRef = useRef<boolean>(onboardingRequired);
  useEffect(() => {
    onboardingRequiredRef.current = onboardingRequired;
  }, [onboardingRequired]);

  useEffect(() => {
    const onChange = () => {
      try {
        setOnboardingRequired(localStorage.getItem('miniapp_onboarding_required') === 'true');
      } catch {
        // noop
      }
    };
    window.addEventListener('miniapp_onboarding_required_changed', onChange);
    return () => window.removeEventListener('miniapp_onboarding_required_changed', onChange);
  }, []);

  const tipsPollTimerRef = useRef<number | null>(null);
  const tipsNextRefreshTimerRef = useRef<number | null>(null);
  const tipsStatusInFlightRef = useRef<Promise<any> | null>(null);

  // Congratulations modal state
  const [showCongrats, setShowCongrats] = useState(false);
  const [achievement, setAchievement] = useState<{
    type: 'weight_goal' | 'streak' | 'calorie_target' | 'first_meal';
    title: string;
    message: string;
    value?: string | number;
  } | null>(null);
  const [shownAchievements, setShownAchievements] = useState<Set<string>>(new Set());

  // Persist achievements shown, so streak/goal popups don't reappear after reload.
  const ACHIEVEMENTS_CACHE_PREFIX = 'miniapp_shown_achievements_v1:';
  const LAST_STREAK_CACHE_PREFIX = 'miniapp_last_streak_v1:';
  const ADVICE_SEEN_CACHE_PREFIX = 'miniapp_seen_advice_key_v1:';
  const ADVICE_SEEN_TG_CACHE_PREFIX = 'miniapp_seen_advice_key_v1_tg:';
  const [achievementsHydrated, setAchievementsHydrated] = useState(false);
  const achievementsUserKeyRef = useRef<string | null>(null);
  const lastStreakRef = useRef<number>(0);
  const seenAdviceKeyRef = useRef<string | null>(null);

  const persistLastStreak = useCallback((userKey: string, streak: number) => {
    try {
      localStorage.setItem(`${LAST_STREAK_CACHE_PREFIX}${userKey}`, String(streak));
    } catch {
      // noop
    }
  }, [LAST_STREAK_CACHE_PREFIX]);

  useEffect(() => {
    const userKey = String(userData?.mini?.user?.id || '').trim();
    if (!userKey) return;

    if (achievementsUserKeyRef.current === userKey && achievementsHydrated) return;

    achievementsUserKeyRef.current = userKey;
    setAchievementsHydrated(false);
    try {
      const raw = localStorage.getItem(`${ACHIEVEMENTS_CACHE_PREFIX}${userKey}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) {
        setShownAchievements(new Set(parsed.filter((v: any) => typeof v === 'string')));
      } else {
        setShownAchievements(new Set());
      }
    } catch {
      setShownAchievements(new Set());
    }

    try {
      const rawLast = localStorage.getItem(`${LAST_STREAK_CACHE_PREFIX}${userKey}`);
      const n = rawLast != null ? Number(rawLast) : 0;
      lastStreakRef.current = Number.isFinite(n) ? n : 0;
    } catch {
      lastStreakRef.current = 0;
    }

    try {
      const rawSeen = localStorage.getItem(`${ADVICE_SEEN_CACHE_PREFIX}${userKey}`);
      seenAdviceKeyRef.current = rawSeen ? String(rawSeen) : null;
    } catch {
      seenAdviceKeyRef.current = null;
    }
    setAchievementsHydrated(true);
  }, [userData?.mini?.user?.id, achievementsHydrated, ADVICE_SEEN_CACHE_PREFIX, LAST_STREAK_CACHE_PREFIX]);

  useEffect(() => {
    const userKey = String(userData?.mini?.user?.id || '').trim();
    if (!userKey || !achievementsHydrated) return;

    try {
      const arr = Array.from(shownAchievements);
      const capped = arr.slice(-200);
      localStorage.setItem(`${ACHIEVEMENTS_CACHE_PREFIX}${userKey}`, JSON.stringify(capped));
    } catch {
      // noop
    }
  }, [shownAchievements, userData?.mini?.user?.id, achievementsHydrated]);

  const _handleRetry = useCallback(() => {
    setReloadTrigger(prev => prev + 1);
  }, []);

  // When activities change (add/delete/mode), reload /me so daily norm updates
  useEffect(() => {
    const handleActivitiesUpdated = () => setReloadTrigger(prev => prev + 1);
    window.addEventListener('activities-updated', handleActivitiesUpdated);
    return () => window.removeEventListener('activities-updated', handleActivitiesUpdated);
  }, []);

  // API клиент
  const api = useApi();

  // Cache: show previous data instantly, then revalidate in background
  const USER_CACHE_KEY = 'miniapp_user_cache_v2';
  const dayCacheRef = useRef<Map<string, { meals: any[]; activities: any | null }>>(new Map());
  const coreLoadRef = useRef<Promise<any> | null>(null);
  const weekLoadRef = useRef<{ key: string | null; promise: Promise<any> | null }>({ key: null, promise: null });
  const sessionStartRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    const revalidate = () => {
      try {
        if (document.visibilityState && document.visibilityState !== 'visible') return;
      } catch {
        // ignore
      }

      // Force re-auth + refetch when Telegram WebView resumes (it can keep JS state alive)
      sessionStartRef.current = null;
      coreLoadRef.current = null;
      weekLoadRef.current = { key: null, promise: null };
      dayCacheRef.current.clear();
      setReloadTrigger((prev) => prev + 1);
    };

    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    window.addEventListener('pageshow', revalidate);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
      window.removeEventListener('pageshow', revalidate);
    };
  }, []);

  useEffect(() => {
    if (!onboardingRequired) return;
    try {
      localStorage.removeItem(USER_CACHE_KEY);
    } catch {
      // noop
    }
    dayCacheRef.current.clear();
    weekLoadRef.current = { key: null, promise: null };
    coreLoadRef.current = null;
    setUserData(null);
  }, [onboardingRequired]);

  const ensureMiniAppAuth = useCallback(async (): Promise<number | null> => {
    const tgData = api.getTelegramData();
    const telegramId = tgData?.user?.id || api.getTelegramUserId() || 845872965;
    
    // Запускаем сессию
    if (!sessionStartRef.current) {
      sessionStartRef.current = (async () => {
        try {
          if (tgData?.initData) {
            await api.startMiniAppSession(tgData.initData);
          } else if (!api.hasAuthToken()) {
            await api.getDevToken(telegramId, 'User', 'user');
          }
        } catch (e) {
          console.warn('MiniApp auth error:', e);
        } finally {
          try {
            const required = api.getMiniAppOnboardingRequired();
            setOnboardingRequired(required === true);
          } catch {
            // noop
          }
        }
      })();
    }

    await sessionStartRef.current;

    // ВСЕГДА возвращаем telegramId - не блокируем
    return telegramId || 845872965;
  }, [api]);

  const isSelectedToday = useMemo(() => {
    const selectedKey = formatLocalDate(selectedDate);
    const todayKey = formatLocalDate(new Date());
    return selectedKey === todayKey;
  }, [selectedDate]);

  const loadAdviceStatus = useCallback(async () => {
    if (onboardingRequiredRef.current) {
      setHasTips(false);
      setTipsEnabled(false);
      return null;
    }

    if (!isSelectedToday) {
      setHasTips(false);
      setTipsEnabled(false);
      return null;
    }

    if (tipsStatusInFlightRef.current) return tipsStatusInFlightRef.current;

    tipsStatusInFlightRef.current = (async () => {
      const telegramId = await ensureMiniAppAuth();
      if (!telegramId) return null;

      if (api.getMiniAppOnboardingRequired() === true) return null;

      // Load seen advice key early (even before userData is fully hydrated)
      try {
        const rawSeen = localStorage.getItem(`${ADVICE_SEEN_TG_CACHE_PREFIX}${telegramId}`);
        if (rawSeen) seenAdviceKeyRef.current = String(rawSeen);
      } catch {
        // noop
      }

      const status = await api.getMiniAppAdviceStatus().catch(() => null);
      if (!status) {
        setHasTips(false);
        setTipsEnabled(false);
        return null;
      }

      const seenKey = seenAdviceKeyRef.current;
      const isNew = !!status.key && !!status.hasAdvice && status.key !== seenKey;
      setHasTips(isNew);
      setTipsEnabled(!!status.hasAdvice);

      if (tipsNextRefreshTimerRef.current) {
        window.clearTimeout(tipsNextRefreshTimerRef.current);
        tipsNextRefreshTimerRef.current = null;
      }

      if (status.nextRefreshAt) {
        const ms = new Date(status.nextRefreshAt).getTime() - Date.now();
        if (Number.isFinite(ms) && ms > 1000) {
          tipsNextRefreshTimerRef.current = window.setTimeout(() => {
            loadAdviceStatus();
          }, ms + 500);
        }
      }

      return status;
    })().finally(() => {
      tipsStatusInFlightRef.current = null;
    });

    return tipsStatusInFlightRef.current;
  }, [api, ensureMiniAppAuth, isSelectedToday]);

  const closeTipsModal = useCallback(() => {
    setTipsModalOpen(false);
    if (!tipsAdviceKey) {
      setHasTips(false);
      return;
    }

    // Save under Telegram ID key (most reliable)
    try {
      const tgUnsafe: any = (window as any).Telegram?.WebApp?.initDataUnsafe;
      const tgId = tgUnsafe?.user?.id;
      if (tgId) {
        localStorage.setItem(`${ADVICE_SEEN_TG_CACHE_PREFIX}${String(tgId)}`, tipsAdviceKey);
      }
    } catch {
      // noop
    }

    const userKey = String(userData?.mini?.user?.id || '').trim();
    if (!userKey) {
      setHasTips(false);
      return;
    }

    try {
      localStorage.setItem(`${ADVICE_SEEN_CACHE_PREFIX}${userKey}`, tipsAdviceKey);
      seenAdviceKeyRef.current = tipsAdviceKey;
    } catch {
      // noop
    }

    setHasTips(false);
  }, [tipsAdviceKey, userData?.mini?.user?.id, ADVICE_SEEN_CACHE_PREFIX, ADVICE_SEEN_TG_CACHE_PREFIX]);

  const saveUserCache = useCallback((data: any) => {
    try {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data));
    } catch {
      // noop
    }
  }, []);

  const loadCoreData = useCallback(async () => {
    if (coreLoadRef.current) return coreLoadRef.current;
    coreLoadRef.current = (async () => {
      const telegramId = await ensureMiniAppAuth();
      if (!telegramId) return null;

      if (api.getMiniAppOnboardingRequired() === true) return null;

      const me = await api.getMiniAppProfile().catch(() => null);
      if (!me) return null;

      setUserData((prev: any) => {
        const prevMini = prev?.mini || {};
        // /miniapp/me does NOT include week charts. Preserve stats loaded from /miniapp/stats.
        const nextMini = {
          ...prevMini,
          ...(me || {}),
          dailyStats: (me as any)?.dailyStats ?? prevMini?.dailyStats,
          weeklyTotals: (me as any)?.weeklyTotals ?? prevMini?.weeklyTotals,
          weeklyAverages: (me as any)?.weeklyAverages ?? prevMini?.weeklyAverages,
        };
        const next = {
          ...(prev || {}),
          // keep legacy shape used across UI
          user: me.user || prev?.user,
          mini: nextMini,
          // some parts still expect weightHistory at root
          weightHistory: (me as any).weightHistory || prev?.weightHistory,
        };
        (globalThis as any)._appUserData = next;
        saveUserCache(next);
        return next;
      });

      return me;
    })().finally(() => {
      coreLoadRef.current = null;
    });

    return coreLoadRef.current;
  }, [api, ensureMiniAppAuth, saveUserCache]);

  const loadDayData = useCallback(async (date: Date) => {
    if (onboardingRequiredRef.current) return;

    const dateStr = formatLocalDate(date);

    // Fast path: show cached day instantly
    const cachedDay = dayCacheRef.current.get(dateStr);
    if (cachedDay) {
      setUserData((prev: any) => {
        const baseTarget = Number(prev?.mini?.profile?.tdee || prev?.mini?.targets?.calories || 0);
        const burned = Number(cachedDay.activities?.summary?.totalCalories || 0);
        const mode = cachedDay.activities?.caloriesMode;
        const targetCalories = mode === 'add_to_norm' ? baseTarget + burned : baseTarget;
        const next = {
          ...(prev || {}),
          todayMeals: cachedDay.meals,
          mini: {
            ...(prev?.mini || {}),
            activities: cachedDay.activities,
            targets: {
              ...(prev?.mini?.targets || {}),
              calories: targetCalories,
            },
          },
        };
        (globalThis as any)._appUserData = next;
        return next;
      });
    }

    await loadCoreData();

    if (api.getMiniAppOnboardingRequired() === true) return;

    const [mealsResp, activitiesResp] = await Promise.all([
      api.getMiniAppMeals(dateStr).catch(() => ({ meals: [] } as any)),
      api.getActivities(dateStr).catch(() => null),
    ]);

    dayCacheRef.current.set(dateStr, { meals: mealsResp?.meals || [], activities: activitiesResp });

    setUserData((prev: any) => {
      const baseTarget = Number(prev?.mini?.profile?.tdee || prev?.mini?.targets?.calories || 0);
      const burned = Number(activitiesResp?.summary?.totalCalories || 0);
      const mode = activitiesResp?.caloriesMode;
      const targetCalories = mode === 'add_to_norm' ? baseTarget + burned : baseTarget;

      const next = {
        ...(prev || {}),
        todayMeals: mealsResp?.meals || [],
        mini: {
          ...(prev?.mini || {}),
          activities: activitiesResp || prev?.mini?.activities,
          targets: {
            ...(prev?.mini?.targets || {}),
            calories: targetCalories,
          },
        },
      };
      (globalThis as any)._appUserData = next;
      saveUserCache(next);
      return next;
    });
  }, [api, loadCoreData, saveUserCache]);

  const loadWeekStats = useCallback(async (anchorDate?: Date) => {
    if (onboardingRequiredRef.current) return null;

    const anchor = anchorDate || new Date();
    const dateKey = formatLocalDate(anchor);

    if (weekLoadRef.current.promise && weekLoadRef.current.key === dateKey) {
      return weekLoadRef.current.promise;
    }

    weekLoadRef.current.key = dateKey;
    weekLoadRef.current.promise = (async () => {
      await loadCoreData();
      if (api.getMiniAppOnboardingRequired() === true) return null;
      const weekStats = await api.getMiniAppStats('week', dateKey).catch(() => null);
      if (!weekStats) return null;

      // Avoid out-of-order updates when user switches dates quickly
      if (weekLoadRef.current.key !== dateKey) return weekStats;

      setUserData((prev: any) => {
        const nextMini: any = {
          ...(prev?.mini || {}),
          dailyStats: weekStats.dailyStats,
          weeklyTotals: weekStats.totals,
          weeklyAverages: weekStats.averages,
          stats: weekStats.stats || prev?.mini?.stats,
        };
        if (!nextMini.targets && weekStats.targets) nextMini.targets = weekStats.targets;

        const next = { ...(prev || {}), mini: nextMini };
        (globalThis as any)._appUserData = next;
        saveUserCache(next);
        return next;
      });

      return weekStats;
    })().finally(() => {
      if (weekLoadRef.current.key === dateKey) {
        weekLoadRef.current.promise = null;
      }
    });

    return weekLoadRef.current.promise;
  }, [api, loadCoreData, saveUserCache]);

  // Expose API client globally for delete functionality
  useEffect(() => {
    (window as any)._apiClient = api;
    return () => {
      delete (window as any)._apiClient;
    };
  }, [api]);

  // Listen for meal-deleted events and revalidate via unified loaders
  useEffect(() => {
    const handleMealDeleted = () => {
      // Drop cached day snapshots so UI + streak refresh immediately after delete
      dayCacheRef.current.clear();
      setReloadTrigger(prev => prev + 1);
    };
    window.addEventListener('meal-deleted', handleMealDeleted);
    return () => window.removeEventListener('meal-deleted', handleMealDeleted);
  }, []);

  // Константы
  const goalCalories = useMemo(() => {
    const val = Number(userData?.mini?.targets?.calories || userData?.mini?.profile?.tdee || 0);
    return Number.isFinite(val) ? val : 0;
  }, [userData]);

  // Instant render: hydrate last known data from localStorage
  useEffect(() => {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        setUserData(parsed);
        (globalThis as any)._appUserData = parsed;
      }
    } catch {
      // noop
    }
  }, []);

  // Unified loaders: core data + selected day data
  useEffect(() => {
    loadCoreData();
  }, [reloadTrigger, loadCoreData]);

  useEffect(() => {
    loadDayData(selectedDate);
  }, [selectedDate, reloadTrigger, loadDayData]);

  // Advice: refresh on load/date change/delete + poll for new meals
  useEffect(() => {
    loadAdviceStatus();
  }, [loadAdviceStatus, reloadTrigger]);

  useEffect(() => {
    if (tipsPollTimerRef.current) {
      window.clearInterval(tipsPollTimerRef.current);
      tipsPollTimerRef.current = null;
    }

    if (tipsNextRefreshTimerRef.current) {
      window.clearTimeout(tipsNextRefreshTimerRef.current);
      tipsNextRefreshTimerRef.current = null;
    }

    if (!isSelectedToday) return;

    tipsPollTimerRef.current = window.setInterval(() => {
      loadAdviceStatus();
    }, 2 * 60 * 1000);

    return () => {
      if (tipsPollTimerRef.current) {
        window.clearInterval(tipsPollTimerRef.current);
        tipsPollTimerRef.current = null;
      }
    };
  }, [isSelectedToday, loadAdviceStatus]);

  // Week stats: lazy (needed mostly for Statistics tab), but prefetch shortly after start
  useEffect(() => {
    if (activeTab === 'statistics') {
      loadWeekStats(selectedDate);
    }
  }, [activeTab, selectedDate, reloadTrigger, loadWeekStats]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      loadWeekStats(new Date()).catch(() => null);
    }, 1200);
    return () => window.clearTimeout(t);
  }, [loadWeekStats]);

  // Check for achievements when userData changes
  useEffect(() => {
    if (!userData?.mini) return;
    if (!achievementsHydrated) return;

    const checkAchievements = () => {
      const profile = userData.mini.profile;
      const stats = userData.mini.stats;
      const today = userData.mini.today;
      const targets = userData.mini.targets;

      if (!profile || !stats) return;

      const userKey = String(userData?.mini?.user?.id || '').trim();
      const todayKey = formatLocalDate(new Date());

      // 1. Check streak milestones (3, 7, 14, 30 days)
      const streakMilestones = [3, 7, 14, 30, 60, 100];
      const currentStreak = stats.streak || 0;

      // Show only when streak grows into a milestone; if streak drops (delete meal) and grows again — show again.
      const prevStreak = Number.isFinite(lastStreakRef.current) ? lastStreakRef.current : 0;
      if (streakMilestones.includes(currentStreak) && currentStreak > prevStreak) {
        setAchievement({
          type: 'streak',
          title: 'Отличная серия!',
          message: `Ты ведёшь дневник питания ${currentStreak} дней подряд!`,
          value: `${currentStreak} 🔥`
        });
        setShowCongrats(true);
        lastStreakRef.current = currentStreak;
        if (userKey) persistLastStreak(userKey, currentStreak);
        return;
      }

      // 2. Check calorie target reached (within 10% of goal)
      if (today && targets) {
        const consumed = today.consumed || 0;
        const target = targets.calories || 0;
        if (target > 0 && consumed >= target * 0.9 && consumed <= target * 1.1) {
          const achievementKey = `calorie_${todayKey}`;
          if (!shownAchievements.has(achievementKey) && consumed > 0) {
            setAchievement({
              type: 'calorie_target',
              title: 'Цель достигнута!',
              message: 'Ты достиг дневной нормы калорий!',
              value: `${Math.round(consumed)} ккал`
            });
            setShowCongrats(true);
            setShownAchievements(prev => new Set(prev).add(achievementKey));
            return;
          }
        }
      }

      // 3. Check weight goal reached (only for lose/gain goals)
      // Skip this check - weight goal notification is handled in onAddWeight with localStorage
      // to ensure it only shows once per target weight

      // Keep last streak in sync (also on decreases), so delete+re-add can trigger milestone again.
      if (userKey) {
        lastStreakRef.current = currentStreak;
        persistLastStreak(userKey, currentStreak);
      }
    };

    // Delay check to avoid showing on initial load flash
    const timer = setTimeout(checkAchievements, 1000);
    return () => clearTimeout(timer);
  }, [userData, shownAchievements, achievementsHydrated, persistLastStreak]);

  // Отдельная догрузка по смене даты не нужна — всё в едином эффекте выше

  // Моки удалены

  // Используем только реальные данные (без моков)
  const todayMeals = useMemo(() => {
    if (userData?.mini && userData?.mini?.profile && userData?.mini?.today) {
      return (userData?.todayMeals || []).map((m: any) => ({
        id: m.id || `${m.category}-${m.createdAt}`,
        name: m.name,
        calories: m.calories,
        protein: m.protein,
        carbs: m.carbs,
        fats: m.fats,
        time: m.time || new Date(m.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
        category: m.category,
        dishes: Array.isArray(m.dishes) ? m.dishes : undefined
      }));
    }
    return [];
  }, [userData]);

  const totalCalories = useMemo(() => {
    // Считаем по списку приёмов за выбранный день, чтобы карточка всегда совпадала с выбранной датой
    return todayMeals.reduce((sum: number, meal: any) => sum + (meal.calories || 0), 0) || 0;
  }, [todayMeals]);

  const dailyData: DailyData[] = useMemo(() => {
    const stats = userData?.mini?.dailyStats;
    if (Array.isArray(stats)) {
      return stats.map((ds: any) => ({
        date: ds.date,
        calories: ds.calories,
        protein: ds.protein,
        carbs: ds.carbs,
        fats: ds.fats,
        meals: [] as any[]
      }));
    }
    return [];
  }, [userData]);

  const weeklyData: WeeklyData[] = useMemo(() => {
    const stats = userData?.mini?.dailyStats;
    const goal = userData?.mini?.targets?.calories || userData?.mini?.profile?.tdee || goalCalories;
    if (Array.isArray(stats)) {
      return stats.map((ds: any) => ({
        day: new Date(ds.date).toLocaleDateString('ru-RU', { weekday: 'short' }),
        calories: ds.calories,
        goal,
        water: 0,
        protein: ds.protein,
        carbs: ds.carbs,
        fats: ds.fats,
      }));
    }
    return [];
  }, [userData, goalCalories]);

  const weightData: WeightData[] = useMemo(() => {
    // Check both top-level weightHistory and mini.weightHistory (from /me)
    const hist = Array.isArray(userData?.weightHistory) ? userData.weightHistory :
      Array.isArray(userData?.mini?.weightHistory) ? userData.mini.weightHistory : [];

    const result = hist.map((w: any) => ({
      date: w.date || w.recordedAt || w.createdAt,
      weight: w.weight,
      bodyFat: w.bodyFat,
      muscleMass: w.muscleMass,
    }));
    // Сортируем по дате по возрастанию для корректных трендов
    result.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
    // Если профиль содержит более актуальный вес — добавим как последнюю точку при расхождении
    const profileWeight = userData?.mini?.profile?.weight;
    const last = result[result.length - 1]?.weight;
    if (typeof profileWeight === 'number' && profileWeight > 0 && (!last || Math.abs(last - profileWeight) > 0.01)) {
      result.push({ date: new Date().toISOString(), weight: profileWeight });
    }
    console.log('AppContent: weightData calculated', result);
    return result;
  }, [userData]);

  // Инициализация Telegram WebApp + deep-link параметры
  useEffect(() => {
    (async () => {
      if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.ready();
        tg.expand();
      }
      // Обработка deep-link параметров (?view=statistics|profile|home), hash, и start_param
      try {
        const applyView = (v?: string | null) => {
          if (v === 'statistics') setActiveTab('statistics');
          else if (v === 'profile') setShowProfile(true);
          else if (v === 'subscription') setShowSubscription(true);
          else if (v === 'offer') setLegalView('offer');
          else if (v === 'privacy') setLegalView('privacy');
        };

        try {
          if (window.location.pathname.includes('/payment/return')) {
            setShowSubscription(true);
          }
        } catch (_) { }

        const params = new URLSearchParams(window.location.search);
        
        // Проверяем startapp
        const startappFromQuery = params.get('startapp');
        if (startappFromQuery) {
          applyView(startappFromQuery);
          if (startappFromQuery === 'profile' && params.get('settings') === 'true') {
            setOpenSettingsOnProfile(true);
          }
        } else {
          applyView(params.get('view'));
          if (params.get('view') === 'profile' && params.get('settings') === 'true') {
            setOpenSettingsOnProfile(true);
          }
        }
        
        // Также проверяем hash
        if (window.location.hash) {
          const hash = new URLSearchParams(window.location.hash.replace(/^#\/?/, ''));
          const hashStartapp = hash.get('startapp');
          if (hashStartapp) {
            applyView(hashStartapp);
          } else if (!startappFromQuery) {
            applyView(hash.get('view'));
          }
          if (hash.get('view') === 'profile' && hash.get('settings') === 'true') {
            setOpenSettingsOnProfile(true);
          }
        }
        
        const tgUnsafe: any = (window as any).Telegram?.WebApp?.initDataUnsafe;
        if (tgUnsafe?.start_param) applyView(tgUnsafe.start_param);
      } catch (_) { }
      // Network loading happens via unified loaders above (core/day/week)
    })();
  }, []);

  // Activity tab is now enabled - no redirect needed

  const navigationItems = useMemo(() => [
    { id: 'home', label: 'Главная', icon: Home, disabled: false },
    { id: 'statistics', label: 'Статистика', icon: BarChart3, disabled: false },
    { id: 'activity', label: 'Активность', icon: Activity, disabled: false },
  ], []);

  // Обработчики событий
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleAddMeal = (mealType: string) => {
    console.log('Adding meal:', mealType);
  };

  const handleViewMeal = (meal: Meal) => {
    console.log('Viewing meal:', meal);
  };

  const handleTipsClick = useCallback(async () => {
    if (!isSelectedToday) return;
    if (!tipsEnabled) return;

    setTipsModalOpen(true);
    setTipsLoading(true);
    setTipsAdviceKey(null);
    try {
      const telegramId = await ensureMiniAppAuth();
      if (!telegramId) throw new Error('No telegramId');

      const advice = await api.getMiniAppAdvice();
      if (!advice?.hasAdvice) {
        setHasTips(false);
        setTipsModalOpen(false);
        return;
      }

      setTipsTitle(advice.title || 'Подсказки на сегодня');
      setTipsList(Array.isArray(advice.tips) ? advice.tips : []);
      setTipsAdviceKey((advice as any).key ? String((advice as any).key) : null);
    } catch (e) {
      console.error('Failed to load advice:', e);
      alert('Не удалось загрузить подсказки');
      setTipsModalOpen(false);
    } finally {
      setTipsLoading(false);
    }
  }, [api, ensureMiniAppAuth, isSelectedToday, tipsEnabled]);

  const handleProfileClick = () => {
    console.log('🔵 Profile button clicked!');
    setShowProfile(true);
    console.log('🔵 showProfile set to true');
  };

  const handleProfileBack = () => {
    console.log('🔴 Closing profile');
    setShowProfile(false);
  };

  const _handleAddActivity = (activity: Omit<ActivityData, 'id'>) => {
    console.log('Adding activity:', activity);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="space-y-6">
            <DailyStats
              totalCalories={totalCalories}
              goalCalories={goalCalories}
              meals={todayMeals}
              protein={{ current: todayMeals.reduce((sum: number, m: any) => sum + (Number(m.protein) || 0), 0), goal: Number(userData?.mini?.targets?.protein) || 0 }}
              carbs={{ current: todayMeals.reduce((sum: number, m: any) => sum + (Number(m.carbs) || 0), 0), goal: Number(userData?.mini?.targets?.carbs) || 0 }}
              fats={{ current: todayMeals.reduce((sum: number, m: any) => sum + (Number(m.fats) || 0), 0), goal: Number(userData?.mini?.targets?.fats) || 0 }}
              dateLabel={selectedDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
              hasTips={hasTips}
              tipsEnabled={tipsEnabled}
              onAddMeal={handleAddMeal}
              onViewMeal={handleViewMeal}
              onTipsClick={handleTipsClick}
            />
          </div>
        );
      case 'statistics':
        return (
          <Statistics
            dailyData={dailyData}
            weeklyData={weeklyData}
            weightData={weightData}
            selectedDate={selectedDate}
            targets={{
              calories: Number(userData?.mini?.targets?.calories) || Number(userData?.mini?.profile?.tdee) || goalCalories,
              protein: Number(userData?.mini?.targets?.protein) || Number(userData?.mini?.profile?.protein) || 0,
              carbs: Number(userData?.mini?.targets?.carbs) || Number(userData?.mini?.profile?.carbs) || 0,
              fats: Number(userData?.mini?.targets?.fats) || Number(userData?.mini?.profile?.fats) || 0
            }}
          />
        );
      case 'activity':
        return (
          <ActivityPage
            selectedDate={selectedDate}
            onDateChange={handleDateSelect}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    console.log('UserData Debug:', userData);
  }, [userData]);

  if (onboardingRequired) {
    const botUrl = 'https://t.me/NomaAiBot';
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial' }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <h1 style={{ fontSize: 26, lineHeight: 1.2, margin: '0 0 12px 0' }}>Noma</h1>
          <p style={{ margin: '0 0 18px 0', opacity: 0.85 }}>
            Пройдите анкету в боте, чтобы пользоваться дневником.
          </p>
          <button
            onClick={() => openExternalLink(botUrl)}
            style={{
              display: 'inline-block',
              background: '#111827',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: 12,
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Открыть бота
          </button>
        </div>
      </div>
    );
  }

  if (legalView === 'offer') {
    return (
      <LegalTextScreen
        title="Публичная оферта"
        text={OFFER_TEXT}
        onBack={() => setLegalView(null)}
      />
    );
  }

  if (legalView === 'privacy') {
    return (
      <LegalTextScreen
        title="Политика конфиденциальности"
        text={PRIVACY_TEXT}
        onBack={() => setLegalView(null)}
      />
    );
  }

  if (showSubscription) {
    return (
      <SubscriptionScreen
        onBack={() => {
          setShowSubscription(false);
          setActiveTab('home');
        }}
        onOpenOffer={() => setLegalView('offer')}
        onOpenPrivacy={() => setLegalView('privacy')}
      />
    );
  }

  if (showProfile) {
    return (
      <>
        <ProfilePage
          onBack={() => {
            setOpenSettingsOnProfile(false);
            handleProfileBack();
          }}
          weightData={weightData}
          achievements={[]}
          challenges={[]}
          onAddWeight={async (weight: number) => {
            try {
              console.log('🔄 Начинаем сохранение веса:', weight);
              const result = await api.addMyWeight(weight);
              console.log('✅ Вес сохранён на сервере:', result);

              // Check if weight goal was reached - show once per target weight
              if (result.goalReached && result.goalMessage) {
                const targetWeight = userData?.mini?.profile?.targetWeight;
                const shownKey = `weight_goal_shown_${targetWeight}`;
                const alreadyShown = localStorage.getItem(shownKey);

                if (!alreadyShown) {
                  localStorage.setItem(shownKey, 'true');
                  setAchievement({
                    type: 'weight_goal',
                    title: 'Цель по весу достигнута!',
                    message: result.goalMessage,
                    value: `${weight} кг`
                  });
                  setShowCongrats(true);
                }
              }

              await loadCoreData();
              await loadWeekStats(selectedDate);
            } catch (e) {
              console.error('❌ Ошибка обновления веса:', e);
              alert('Не удалось сохранить вес.');
            }
          }}
          onJoinChallenge={() => { }}
          onUpdateSettings={() => { }}
          onSendMessage={() => { }}
          profile={{
            name: userData?.user?.firstName ? `${userData.user.firstName} ${userData.user.lastName || ''}`.trim() : 'User',
            username: userData?.user?.username || 'user',
            isPremium: userData?.user?.isPremium || false,
            trialEndsAt: userData?.user?.trialEndsAt || null,
            subscriptionEndsAt: userData?.user?.subscriptionEndsAt || null,
            currentWeight: userData?.mini?.profile?.weight || 75,
            goalWeight: userData?.mini?.profile?.targetWeight || 70,
            height: userData?.mini?.profile?.height || 175,
            age: userData?.mini?.profile?.age || 25,
            birthDate: userData?.mini?.profile?.birthDate || null,
            gender: userData?.mini?.profile?.gender || 'male',
            activityLevel: userData?.mini?.profile?.activityLevel || 'moderate',
            goal: userData?.mini?.profile?.goal || 'maintain',
            pace: userData?.mini?.profile?.pace || 'balanced',
            tdee: userData?.mini?.profile?.tdee || 2000,
            protein: userData?.mini?.targets?.protein || 120,
            carbs: userData?.mini?.targets?.carbs || 250,
            fats: userData?.mini?.targets?.fats || 70,
            customKBJU: userData?.mini?.profile?.customKBJU || null,
            notificationSettings: userData?.mini?.profile?.notificationSettings || null,
            stats: userData?.mini?.stats || { totalDays: 0, streak: 0 }
          }}
          onProfileUpdate={async () => {
            // Refresh data after profile update (targets/tdee can change)
            await loadCoreData();
            await Promise.all([
              loadDayData(selectedDate),
              loadWeekStats(selectedDate),
            ]);
          }}
          initialShowSettings={openSettingsOnProfile}
        />
        {/* Congratulations Modal - must be outside ProfilePage to show on top */}
        <CongratulationsModal
          isOpen={showCongrats}
          onClose={() => setShowCongrats(false)}
          onUpdateGoal={() => {
            setOpenSettingsOnProfile(true);
          }}
          achievement={achievement}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Календарь */}
      <Calendar
        currentDate={selectedDate}
        onDateSelect={handleDateSelect}
        onProfileClick={handleProfileClick}
        availableDates={new Set((userData?.mini?.dailyStats || []).map((d: any) => d.date))}
      />
      <div className="text-center text-[10px] text-muted-foreground/30 py-1">v1.27.1</div>

      {/* Debug Info удален по просьбе пользователя */}
      {/* Основной контент с правильным отступом */}
      <div className="px-4 app-content">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeTab}
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Исправленная нижняя навигация */}
      <div className="bottom-navigation">
        <div className="nav-container">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => !item.disabled && setActiveTab(item.id)}
              disabled={item.disabled}
              className={`nav-button ${activeTab === item.id ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
            >
              <item.icon className="nav-button-icon" />
              <span className="nav-button-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tips Modal */}
      <TipsModal
        isOpen={tipsModalOpen}
        loading={tipsLoading}
        title={tipsTitle}
        tips={tipsList}
        onClose={closeTipsModal}
      />

      {/* Congratulations Modal */}
      <CongratulationsModal
        isOpen={showCongrats}
        onClose={() => setShowCongrats(false)}
        onUpdateGoal={() => {
          setOpenSettingsOnProfile(true);
          setShowProfile(true);
        }}
        achievement={achievement}
      />
    </div>
  );
}

// ===== EXPORT DEFAULT APP =====
export default function App() {
  // ПРОСТО возвращаем приложение без проверок
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}